import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  Collapse,
  Breadcrumbs,
  Link,
  Card,
  CardContent,
  IconButton,
  useTheme,
  useMediaQuery
} from '@mui/material';

// Icons
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import BuildIcon from '@mui/icons-material/Build';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ApiIcon from '@mui/icons-material/Api';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import MemoryIcon from '@mui/icons-material/Memory';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import SecurityIcon from '@mui/icons-material/Security';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MenuIcon from '@mui/icons-material/Menu';

// Documentation sections structure
const docSections = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: <MenuBookIcon />,
    subsections: [
      { id: 'introduction', title: 'Introduction' },
      { id: 'quick-start', title: 'Quick Start Guide' },
      { id: 'key-concepts', title: 'Key Concepts' },
      { id: 'account-setup', title: 'Account Setup' }
    ]
  },
  {
    id: 'platform-overview',
    title: 'Platform Overview',
    icon: <BuildIcon />,
    subsections: [
      { id: 'dashboard', title: 'Trading Dashboard' },
      { id: 'strategy-monitor', title: 'Strategy Monitor' },
      { id: 'risk-management', title: 'Risk Management' },
      { id: 'trade-history', title: 'Trade History' },
      { id: 'execution', title: 'Execution' }
    ]
  },
  {
    id: 'strategy-builder',
    title: 'Strategy Builder',
    icon: <AccountTreeIcon />,
    subsections: [
      { id: 'nodes-overview', title: 'Nodes Overview' },
      { id: 'creating-strategies', title: 'Creating Strategies' },
      { id: 'templates', title: 'Strategy Templates' },
      { id: 'node-connections', title: 'Node Connections' },
      { id: 'scheduler', title: 'Scheduler' },
      { id: 'rescue-system', title: 'Rescue System' }
    ]
  },
  {
    id: 'node-types',
    title: 'Node Types',
    icon: <MemoryIcon />,
    subsections: [
      { id: 'data-source-node', title: 'Data Source' },
      { id: 'technical-analyst-node', title: 'Technical Analyst' },
      { id: 'condition-node', title: 'Condition' },
      { id: 'risk-manager-node', title: 'Risk Manager' },
      { id: 'signal-matrix-node', title: 'Signal Matrix' },
      { id: 'execution-node', title: 'Execution' },
      { id: 'rescue-manager-node', title: 'Rescue Manager' },
      { id: 'report-synthesizer-node', title: 'Report Synthesizer' }
    ]
  },
  {
    id: 'broker-integrations',
    title: 'Broker Integrations',
    icon: <AccountBalanceIcon />,
    subsections: [
      { id: 'binance', title: 'Binance' },
      { id: 'kucoin', title: 'KuCoin' },
      { id: 'bybit', title: 'Bybit' },
      { id: 'okx', title: 'OKX' },
      { id: 'alpaca', title: 'Alpaca' }
    ]
  },
  {
    id: 'api-integrations',
    title: 'API & Integrations',
    icon: <ApiIcon />,
    subsections: [
      { id: 'telegram-reporting', title: 'Telegram Reporting' },
      { id: 'api-documentation', title: 'API Documentation' },
      { id: 'webhooks', title: 'Webhooks' }
    ]
  },
  {
    id: 'configuration',
    title: 'Configuration',
    icon: <SecurityIcon />,
    subsections: [
      { id: 'system-settings', title: 'System Settings' },
      { id: 'api-keys', title: 'API Keys Management' },
      { id: 'risk-settings', title: 'Risk Settings' },
      { id: 'notifications', title: 'Notifications' },
      { id: 'account-settings', title: 'Account Settings' }
    ]
  },
  {
    id: 'guides-tutorials',
    title: 'Guides & Tutorials',
    icon: <ShowChartIcon />,
    subsections: [
      { id: 'first-strategy', title: 'Your First Strategy' },
      { id: 'scalping-guide', title: 'Scalping Strategies' },
      { id: 'trend-following', title: 'Trend Following' },
      { id: 'paper-trading', title: 'Paper Trading' },
      { id: 'going-live', title: 'Going Live' }
    ]
  },
  {
    id: 'developers',
    title: 'Developers',
    icon: <IntegrationInstructionsIcon />,
    subsections: [
      { id: 'architecture', title: 'System Architecture' },
      { id: 'cloudflare-stack', title: 'Cloudflare Stack' },
      { id: 'custom-nodes', title: 'Creating Custom Nodes' },
      { id: 'api-reference', title: 'API Reference' }
    ]
  },
  {
    id: 'faqs',
    title: 'FAQs & Troubleshooting',
    icon: <HelpOutlineIcon />,
    subsections: [
      { id: 'common-issues', title: 'Common Issues' },
      { id: 'performance', title: 'Performance Optimization' },
      { id: 'security-best-practices', title: 'Security Best Practices' },
      { id: 'support', title: 'Getting Support' }
    ]
  }
];

// Content for each section
const docContent = {
  'key-concepts': {
    title: 'Key Concepts',
    content: `
      <h2>Key Concepts in AI Trading System</h2>
      <p>Understanding these fundamental concepts will help you get the most out of the AI Trading System platform.</p>
      
      <h3>Node-Based Strategy Building</h3>
      <p>The AI Trading System uses a visual node-based approach to strategy creation, where:</p>
      <ul>
        <li><strong>Nodes</strong> represent individual components with specific functionality (data sources, analysis tools, execution engines, etc.)</li>
        <li><strong>Edges</strong> connect nodes and represent the flow of data or signals between them</li>
        <li><strong>Node parameters</strong> allow you to customize the behavior of each node</li>
      </ul>
      <p>This approach allows you to build complex strategies without writing code.</p>
      
      <h3>AI-Assisted Trading</h3>
      <p>The platform leverages OpenAI's capabilities to:</p>
      <ul>
        <li><strong>Analyze market data</strong> using technical and fundamental approaches</li>
        <li><strong>Generate trading signals</strong> based on pattern recognition</li>
        <li><strong>Evaluate risk</strong> for each potential trade</li>
        <li><strong>Provide explanations</strong> for trading decisions</li>
      </ul>
      <p>Each AI component uses a dedicated OpenAI Assistant configured for its specific task.</p>
      
      <h3>Multi-Broker Architecture</h3>
      <p>The system supports multiple brokers simultaneously, allowing you to:</p>
      <ul>
        <li>Trade across different asset classes (stocks, crypto, etc.)</li>
        <li>Execute the same strategy on multiple venues</li>
        <li>Optimize for execution quality by broker</li>
        <li>Maintain separate risk parameters for each broker</li>
      </ul>
      
      <h3>Separation of Concerns</h3>
      <p>The platform separates different aspects of trading:</p>
      <ul>
        <li><strong>Data Collection</strong>: Gathering market data, news, and information</li>
        <li><strong>Analysis</strong>: Interpreting data to find patterns and opportunities</li>
        <li><strong>Signal Generation</strong>: Creating specific buy/sell recommendations</li>
        <li><strong>Risk Management</strong>: Determining position size and risk controls</li>
        <li><strong>Execution</strong>: Placing and managing orders with brokers</li>
        <li><strong>Reporting</strong>: Tracking performance and communicating results</li>
      </ul>
      <p>This modular approach allows for better testing, maintenance, and customization.</p>
    `
  },
  'risk-management': {
    title: 'Risk Management',
    content: `
      <h2>Risk Management</h2>
      <p>The Risk Management module provides tools to protect your capital and optimize position sizing for all trading activities.</p>
      
      <h3>Position Sizing</h3>
      
      <h4>Sizing Methods</h4>
      <ul>
        <li><strong>Fixed Risk</strong>: Risk a set percentage of account equity per trade</li>
        <li><strong>Fixed Position</strong>: Trade fixed position sizes regardless of volatility</li>
        <li><strong>Volatility-Based</strong>: Adjust position size based on market volatility (ATR)</li>
        <li><strong>Kelly Criterion</strong>: Optimize position sizes based on expected win rate and risk/reward ratio</li>
      </ul>
      
      <h4>Maximum Exposure Rules</h4>
      <ul>
        <li><strong>Per Position</strong>: Limit maximum size for any single position</li>
        <li><strong>Per Asset</strong>: Limit exposure to any single asset across strategies</li>
        <li><strong>Per Sector</strong>: Limit aggregate exposure to related assets</li>
        <li><strong>Total Portfolio</strong>: Cap overall leverage or market exposure</li>
      </ul>
      
      <h3>Stop Loss Management</h3>
      <ul>
        <li><strong>Fixed Stop Loss</strong>: Set stops at a specific price or percentage from entry</li>
        <li><strong>Technical Stop Loss</strong>: Place stops based on support/resistance levels or indicators</li>
        <li><strong>Volatility-Based Stops</strong>: Dynamic stops calculated using ATR or standard deviation</li>
        <li><strong>Trailing Stops</strong>: Automatically adjust stops to lock in profits as price moves favorably</li>
      </ul>
      
      <h3>Profit Taking</h3>
      <ul>
        <li><strong>Fixed Take Profit</strong>: Exit at predetermined price targets</li>
        <li><strong>Partial Profit Taking</strong>: Scale out of positions at multiple price levels</li>
        <li><strong>Indicator-Based Exits</strong>: Exit when technical indicators signal reversal</li>
        <li><strong>Time-Based Exits</strong>: Close positions after a specific duration</li>
      </ul>
      
      <h3>AI-Powered Risk Assessment</h3>
      <ul>
        <li><strong>Risk Manager AI</strong>: OpenAI-powered analysis of risk factors for each trade</li>
        <li><strong>Risk Scoring</strong>: AI-generated risk score for potential trades</li>
        <li><strong>Correlation Analysis</strong>: Identify hidden correlations between positions</li>
        <li><strong>Market Regime Detection</strong>: Adjust risk parameters based on current market conditions</li>
      </ul>
      
      <h3>Integration with Strategy Builder</h3>
      <p>Risk Management integrates with the Strategy Builder through the Risk Manager node:</p>
      <ul>
        <li>Configure risk parameters for each strategy</li>
        <li>Connect signal nodes to risk manager for position sizing</li>
        <li>Set strategy-specific stop loss rules</li>
        <li>Define profit taking methodologies</li>
      </ul>
      
      <h3>Risk Reporting</h3>
      <ul>
        <li><strong>Real-time Exposure</strong>: Current risk levels across all positions</li>
        <li><strong>VaR Analysis</strong>: Value at Risk calculations for portfolio</li>
        <li><strong>Drawdown Monitoring</strong>: Track and alert on drawdown thresholds</li>
        <li><strong>Stress Testing</strong>: Simulate portfolio performance under extreme market conditions</li>
      </ul>
    `
  },
  'execution': {
    title: 'Execution',
    content: `
      <h2>Execution</h2>
      <p>The Execution module handles the actual placement and management of orders with connected brokers, ensuring strategies are executed efficiently and reliably.</p>
      
      <h3>Order Types</h3>
      
      <h4>Basic Order Types</h4>
      <ul>
        <li><strong>Market Orders</strong>: Immediate execution at current market price</li>
        <li><strong>Limit Orders</strong>: Execution at specified price or better</li>
        <li><strong>Stop Orders</strong>: Convert to market order when price reaches trigger level</li>
        <li><strong>Stop-Limit Orders</strong>: Convert to limit order when price reaches trigger level</li>
      </ul>
      
      <h4>Advanced Order Types</h4>
      <ul>
        <li><strong>Trailing Stop Orders</strong>: Dynamic stops that adjust with favorable price movement</li>
        <li><strong>OCO (One-Cancels-Other)</strong>: Pair of orders where execution of one cancels the other</li>
        <li><strong>Iceberg Orders</strong>: Large orders divided into smaller visible portions to minimize market impact</li>
        <li><strong>TWAP/VWAP Orders</strong>: Time or volume-weighted average price execution strategies</li>
      </ul>
      
      <h3>Execution Modes</h3>
      <ul>
        <li><strong>Fully Automated</strong>: Orders placed without human intervention</li>
        <li><strong>Semi-Automated</strong>: Requires manual confirmation before order placement</li>
        <li><strong>Manual with Suggestions</strong>: Provides trade recommendations for manual execution</li>
        <li><strong>Paper Trading</strong>: Simulated execution without real money</li>
      </ul>
      
      <h3>Multi-Broker Support</h3>
      <p>The Execution module can route orders to various brokers based on:</p>
      <ul>
        <li><strong>Asset Availability</strong>: Automatically select broker that offers the asset</li>
        <li><strong>Cost Optimization</strong>: Route to broker with lowest fees or best execution</li>
        <li><strong>Account Preferences</strong>: Use specific accounts for different strategies</li>
        <li><strong>Smart Routing</strong>: Split orders across multiple brokers for large positions</li>
      </ul>
      
      <h3>Trade Parameters</h3>
      <ul>
        <li><strong>Risk Level</strong>: Determine the percentage of account to risk per trade</li>
        <li><strong>Maximum Trade Amount</strong>: Cap on position size regardless of signals</li>
        <li><strong>Default Trading Pair</strong>: Default instrument to trade when not specified</li>
        <li><strong>Trade Interval</strong>: Frequency of trade execution for systematic strategies</li>
      </ul>
      
      <h3>AI-Powered Execution</h3>
      <ul>
        <li><strong>Execution Agent AI</strong>: OpenAI-powered optimization of execution timing and method</li>
        <li><strong>Slippage Reduction</strong>: AI algorithms to minimize execution costs</li>
        <li><strong>Market Impact Analysis</strong>: Predictive assessment of order impact on price</li>
        <li><strong>Execution Quality Reports</strong>: AI-generated analysis of execution performance</li>
      </ul>
      
      <h3>Integration with Strategy Builder</h3>
      <p>Execution integrates with the Strategy Builder through the Execution Layer node:</p>
      <ul>
        <li>Configure execution parameters specific to each strategy</li>
        <li>Connect risk management output to execution decisions</li>
        <li>Set broker preferences and order types</li>
        <li>Define confirmation requirements and automation level</li>
      </ul>
      
      <h3>Order Monitoring and Management</h3>
      <ul>
        <li><strong>Order Status Tracking</strong>: Real-time updates on all pending and completed orders</li>
        <li><strong>Position Management</strong>: View and manage all open positions</li>
        <li><strong>Order Modification</strong>: Update pending orders with new parameters</li>
        <li><strong>Emergency Controls</strong>: Quickly flatten all positions or cancel all pending orders</li>
      </ul>
    `
  },
  'system-settings': {
    title: 'System Settings',
    content: `
      <h2>System Settings</h2>
      <p>The System Settings section allows you to configure all aspects of the AI Trading System platform to meet your specific requirements.</p>
      
      <h3>API Keys Management</h3>
      
      <h4>Broker API Keys</h4>
      <p>Configure credentials for various brokers and exchanges:</p>
      <ul>
        <li><strong>Alpaca</strong>: API Key and Secret Key</li>
        <li><strong>Binance</strong>: API Key and Secret Key</li>
        <li><strong>KuCoin</strong>: API Key, Secret Key, and Passphrase</li>
        <li><strong>Coinbase Pro</strong>: API Key, Secret Key, and Passphrase</li>
        <li><strong>Kraken</strong>: API Key and Private Key</li>
        <li><strong>Interactive Brokers</strong>: Account ID and API Connection Setup</li>
      </ul>
      <p>Each broker configuration includes a Test Connection button to verify credentials.</p>
      
      <h4>AI Service Keys</h4>
      <ul>
        <li><strong>OpenAI API Key</strong>: For accessing GPT models and assistants</li>
        <li><strong>Assistant IDs</strong>: Configuration for each AI agent node</li>
      </ul>
      
      <h3>Webhook Configurations</h3>
      <ul>
        <li><strong>Telegram Webhooks</strong>: Setup for Telegram bot notifications</li>
        <li><strong>Custom Webhooks</strong>: Configuration for third-party service integration</li>
        <li><strong>Incoming Webhooks</strong>: Receive signals from external sources</li>
      </ul>
      
      <h3>Notification Settings</h3>
      <ul>
        <li><strong>Email Notifications</strong>: Configuration for trade alerts, system updates, etc.</li>
        <li><strong>Telegram Integration</strong>: Bot token and chat ID for Telegram reporting</li>
        <li><strong>SMS Alerts</strong>: Phone number and preferences for critical alerts</li>
        <li><strong>In-App Notifications</strong>: Priority settings for application notifications</li>
      </ul>
      
      <h3>Data Sources</h3>
      <ul>
        <li><strong>Market Data Providers</strong>: Configure data feeds for various asset classes</li>
        <li><strong>News Sources</strong>: API keys for financial news services</li>
        <li><strong>Economic Calendar</strong>: Settings for economic data releases and events</li>
        <li><strong>Alternative Data</strong>: Configuration for non-traditional data sources</li>
      </ul>
      
      <h3>System Preferences</h3>
      <ul>
        <li><strong>User Interface</strong>: Theme selection, layout preferences, display options</li>
        <li><strong>Time Zone</strong>: Set local time zone for system operations</li>
        <li><strong>Date Format</strong>: Configure date and time display format</li>
        <li><strong>Currency Display</strong>: Set base currency and display preferences</li>
      </ul>
      
      <h3>Backup and Storage</h3>
      <ul>
        <li><strong>Strategy Backup</strong>: Automated backup frequency and location</li>
        <li><strong>Trade History Export</strong>: Settings for periodic data exports</li>
        <li><strong>Log Retention</strong>: Configure how long system logs are kept</li>
        <li><strong>Cloud Sync</strong>: Settings for syncing data across multiple devices</li>
      </ul>
      
      <h3>Access Control</h3>
      <ul>
        <li><strong>User Management</strong>: Add, remove, or modify user accounts</li>
        <li><strong>Permission Levels</strong>: Configure access rights for different users</li>
        <li><strong>Authentication Settings</strong>: Two-factor authentication, session timeouts</li>
        <li><strong>API Access</strong>: Manage external API access to the system</li>
      </ul>
    `
  },
  'trade-history': {
    title: 'Trade History',
    content: `
      <h2>Trade History</h2>
      <p>The Trade History module provides comprehensive tracking, analysis, and reporting of all your trading activities.</p>
      
      <h3>Trade Logging</h3>
      
      <h4>Trade Details</h4>
      <ul>
        <li><strong>Basic Information</strong>: Symbol, side (buy/sell), price, quantity, timestamp</li>
        <li><strong>Order Details</strong>: Order type, execution method, fees, slippage</li>
        <li><strong>Strategy Context</strong>: Which strategy initiated the trade, signal source</li>
        <li><strong>Risk Parameters</strong>: Stop loss, take profit, risk amount, R-multiple</li>
      </ul>
      
      <h4>Multi-Broker Syncing</h4>
      <ul>
        <li>Automatic synchronization of trades from all connected brokers</li>
        <li>Manual trade entry for external or non-connected accounts</li>
        <li>Reconciliation tools to ensure all trades are properly recorded</li>
      </ul>
      
      <h3>Performance Analytics</h3>
      
      <h4>Trade Statistics</h4>
      <ul>
        <li><strong>Profitability Metrics</strong>: Win rate, average win/loss, expectancy, Sharpe ratio</li>
        <li><strong>Time Analysis</strong>: Performance by day of week, time of day, month</li>
        <li><strong>Instrument Analysis</strong>: Performance by symbol, asset class, sector</li>
        <li><strong>Strategy Analysis</strong>: Performance breakdown by trading strategy</li>
      </ul>
      
      <h4>Detailed Visualizations</h4>
      <ul>
        <li><strong>Equity Curve</strong>: Visual representation of account growth over time</li>
        <li><strong>Drawdown Chart</strong>: Historical drawdowns with duration metrics</li>
        <li><strong>Profit Distribution</strong>: Histogram of trade P&L outcomes</li>
        <li><strong>Heat Maps</strong>: Visual patterns of trading performance</li>
      </ul>
      
      <h3>Trade Review Tools</h3>
      
      <h4>Individual Trade Analysis</h4>
      <ul>
        <li><strong>Trade Replay</strong>: Visualize how the trade played out on a chart</li>
        <li><strong>Market Context</strong>: News, events, and market conditions during the trade</li>
        <li><strong>Signal Quality</strong>: Assessment of the original trading signal</li>
        <li><strong>Execution Quality</strong>: Analysis of entry/exit timing and slippage</li>
      </ul>
      
      <h4>Journaling Features</h4>
      <ul>
        <li><strong>Trade Notes</strong>: Add subjective notes and observations for each trade</li>
        <li><strong>Screenshots</strong>: Attach chart images and other visual records</li>
        <li><strong>Tags</strong>: Categorize trades with custom tags for better analysis</li>
        <li><strong>Emotional State</strong>: Track psychological factors affecting trading decisions</li>
      </ul>
      
      <h3>AI-Powered Insights</h3>
      <ul>
        <li><strong>Trade Historian AI</strong>: OpenAI-powered analysis of trading patterns and biases</li>
        <li><strong>Performance Recommendations</strong>: AI-generated suggestions for improvement</li>
        <li><strong>Pattern Recognition</strong>: Identification of recurring issues or strengths</li>
        <li><strong>Natural Language Querying</strong>: Ask questions about trading history in plain English</li>
      </ul>
      
      <h3>Reporting</h3>
      <ul>
        <li><strong>Periodic Reports</strong>: Daily, weekly, monthly performance summaries</li>
        <li><strong>Tax Documents</strong>: Export trade data for tax reporting purposes</li>
        <li><strong>Custom Reports</strong>: Generate reports with user-selected metrics and timeframes</li>
        <li><strong>Report Sharing</strong>: Securely share reports with team members or accountants</li>
      </ul>
    `
  },
  'dashboard': {
    title: 'Trading Dashboard',
    content: `
      <h2>Trading Dashboard</h2>
      <p>The Trading Dashboard is your command center for monitoring active strategies, open positions, and real-time performance metrics.</p>
      
      <h3>Main Components</h3>
      
      <h4>Active Strategies Panel</h4>
      <ul>
        <li><strong>Strategy Cards:</strong> Each running strategy displays as a card with status, P&L, and controls</li>
        <li><strong>Paper/Live Toggle:</strong> Switch between paper trading and live trading modes</li>
        <li><strong>Active/Pause Toggle:</strong> Temporarily pause a strategy without stopping it</li>
        <li><strong>Quick Actions:</strong> Stop, edit, or view strategy details</li>
      </ul>
      
      <h4>Open Positions</h4>
      <ul>
        <li><strong>Position List:</strong> All currently open positions across all strategies</li>
        <li><strong>Real-time P&L:</strong> Live profit/loss updates for each position</li>
        <li><strong>Entry Details:</strong> Entry price, size, leverage, and timestamp</li>
        <li><strong>Risk Indicators:</strong> Stop-loss, take-profit levels, and current risk status</li>
        <li><strong>Manual Close:</strong> Close any position manually with one click</li>
      </ul>
      
      <h4>Performance Summary</h4>
      <ul>
        <li><strong>Total P&L:</strong> Combined profit/loss across all positions</li>
        <li><strong>Win Rate:</strong> Percentage of profitable trades</li>
        <li><strong>Average Trade:</strong> Mean profit/loss per trade</li>
        <li><strong>Equity Curve:</strong> Visual chart of account performance over time</li>
      </ul>
      
      <h4>Market Sessions Monitor</h4>
      <ul>
        <li><strong>Global Sessions:</strong> Sydney, Tokyo, Frankfurt, London, New York market hours</li>
        <li><strong>Session Status:</strong> Open, closed, or pre-market indicators</li>
        <li><strong>Golden Hours:</strong> Highlighted high-volatility trading windows</li>
        <li><strong>Countdown Timers:</strong> Time until next session opens/closes</li>
      </ul>
      
      <h3>Position Management</h3>
      <p>From the dashboard, you can manage positions directly:</p>
      <ul>
        <li><strong>Add Budget:</strong> Manually add funds to a position (for rescue operations)</li>
        <li><strong>Modify SL/TP:</strong> Adjust stop-loss and take-profit levels</li>
        <li><strong>Close Position:</strong> Exit a position at market price</li>
        <li><strong>View Details:</strong> See full position history and events</li>
      </ul>
    `
  },
  'strategy-monitor': {
    title: 'Strategy Monitor',
    content: `
      <h2>Strategy Monitor</h2>
      <p>The Strategy Monitor provides real-time visibility into your strategy execution using Server-Sent Events (SSE) for live streaming updates.</p>
      
      <h3>Real-Time Event Stream</h3>
      <p>Watch your strategies execute in real-time with live event updates:</p>
      <ul>
        <li><strong>Scan Events:</strong> Market scans and signal generation</li>
        <li><strong>Signal Events:</strong> Buy/sell signals with confidence scores</li>
        <li><strong>Order Events:</strong> Order placement and execution</li>
        <li><strong>Position Events:</strong> Position opens, updates, and closes</li>
        <li><strong>Risk Events:</strong> Stop-loss triggers, take-profit hits, rescue activations</li>
        <li><strong>Error Events:</strong> Any errors or warnings during execution</li>
      </ul>
      
      <h3>Event Details</h3>
      <p>Each event includes detailed information:</p>
      <ul>
        <li><strong>Timestamp:</strong> Exact time of the event</li>
        <li><strong>Strategy:</strong> Which strategy generated the event</li>
        <li><strong>Symbol:</strong> The trading pair involved</li>
        <li><strong>Type:</strong> Event category (scan, signal, order, etc.)</li>
        <li><strong>Details:</strong> Specific event data (prices, sizes, reasons)</li>
      </ul>
      
      <h3>Filtering Events</h3>
      <ul>
        <li><strong>By Strategy:</strong> Show events from specific strategies only</li>
        <li><strong>By Type:</strong> Filter to show only signals, orders, or errors</li>
        <li><strong>By Symbol:</strong> Focus on specific trading pairs</li>
        <li><strong>Time Range:</strong> View events from a specific time period</li>
      </ul>
      
      <h3>Connection Status</h3>
      <p>The monitor shows your connection status:</p>
      <ul>
        <li><strong>Connected:</strong> Green indicator - receiving live events</li>
        <li><strong>Reconnecting:</strong> Yellow indicator - attempting to reconnect</li>
        <li><strong>Disconnected:</strong> Red indicator - no connection</li>
      </ul>
      
      <h3>Event History</h3>
      <p>Recent events are cached locally for review:</p>
      <ul>
        <li>Last 1000 events stored in browser</li>
        <li>Scroll back to review past events</li>
        <li>Export events to JSON for analysis</li>
      </ul>
    `
  },
  'technical-strategies': {
    title: 'Technical Trading Strategies',
    content: `
      <h2>Technical Trading Strategies</h2>
      <p>Learn how to build effective technical analysis strategies with the AI Trading System platform.</p>
      
      <h3>Moving Average Crossover Strategy</h3>
      <p>A popular strategy based on the crossover of two moving averages of different periods.</p>
      <ol>
        <li><strong>Setup:</strong> Use a Technical Analyst node with parameters for MA(50) and MA(200)</li>
        <li><strong>Signal Logic:</strong> Generate a buy signal when MA(50) crosses above MA(200), and a sell signal when it crosses below</li>
        <li><strong>Risk Management:</strong> Set stop-loss at recent swing low for buy orders, or swing high for sell orders</li>
        <li><strong>Position Sizing:</strong> Risk no more than 1-2% of account per trade</li>
      </ol>
      
      <h3>RSI Overbought/Oversold Strategy</h3>
      <p>A mean-reversion strategy using the Relative Strength Index (RSI) indicator.</p>
      <ol>
        <li><strong>Setup:</strong> Configure a Technical Analyst node to monitor RSI(14)</li>
        <li><strong>Signal Logic:</strong> Generate buy signals when RSI drops below 30 and begins to rise, sell signals when RSI rises above 70 and begins to fall</li>
        <li><strong>Confirmation:</strong> Add a second Technical Analyst node to confirm trend direction</li>
        <li><strong>Execution:</strong> Set limit orders to capture best entry prices</li>
      </ol>
      
      <h3>MACD Momentum Strategy</h3>
      <p>A trend-following strategy using the Moving Average Convergence Divergence (MACD) indicator.</p>
      <ol>
        <li><strong>Setup:</strong> Configure a Technical Analyst node to calculate MACD with standard parameters (12, 26, 9)</li>
        <li><strong>Signal Logic:</strong> Generate buy signals when MACD line crosses above signal line in a positive direction, sell signals when it crosses below</li>
        <li><strong>Filter:</strong> Only take trades in the direction of the overall trend (use a longer-term moving average to determine)</li>
        <li><strong>Exit Strategy:</strong> Exit when MACD crosses back in the opposite direction or when a predetermined profit target is reached</li>
      </ol>
      
      <h3>Bollinger Band Squeeze Strategy</h3>
      <p>A volatility breakout strategy using Bollinger Bands and Keltner Channels.</p>
      <ol>
        <li><strong>Setup:</strong> Use a Technical Analyst node to identify when Bollinger Bands contract inside Keltner Channels (indicating low volatility)</li>
        <li><strong>Signal Logic:</strong> Wait for a breakout from the squeeze, then enter in the direction of the breakout</li>
        <li><strong>Confirmation:</strong> Add volume analysis to confirm the breakout is valid</li>
        <li><strong>Risk Management:</strong> Set stop-loss at the opposite side of the bands/channels</li>
      </ol>
    `
  },
  'quick-start': {
    title: 'Quick Start Guide',
    content: `
      <h2>Quick Start Guide</h2>
      <p>Get up and running with AI Trading System in under 10 minutes. This guide walks you through the basic setup and creating your first strategy.</p>
      
      <h3>Step 1: Configure API Keys</h3>
      <ol>
        <li>Navigate to the <strong>Settings</strong> page.</li>
        <li>Under the <strong>Brokers</strong> tab, select your preferred broker.</li>
        <li>Enter your API key and secret.</li>
        <li>Click <strong>Test Connection</strong> to verify.</li>
      </ol>
      
      <h3>Step 2: OpenAI Integration</h3>
      <ol>
        <li>Under the <strong>API Keys</strong> section, enter your OpenAI API key.</li>
        <li>Test the connection to ensure it works.</li>
      </ol>
      
      <h3>Step 3: Create Your First Strategy</h3>
      <ol>
        <li>Navigate to <strong>Strategy Builder</strong>.</li>
        <li>Click <strong>New Strategy</strong> or select a template.</li>
        <li>Drag and connect nodes to create your strategy flow.</li>
        <li>Configure each node by clicking on it and adjusting parameters.</li>
        <li>Save your strategy with a descriptive name.</li>
      </ol>
    `
  },
  'templates': {
    title: 'Strategy Templates',
    content: `
      <h2>Strategy Templates</h2>
      <p>Save and reuse your strategy configurations as templates. Templates are stored per-user and can be quickly loaded to create new strategies.</p>
      
      <h3>Saving a Template</h3>
      <ol>
        <li>Build your strategy in the Strategy Builder</li>
        <li>Click <strong>Save as Template</strong> in the toolbar</li>
        <li>Enter a template name and description</li>
        <li>Click <strong>Save</strong> to store the template</li>
      </ol>
      
      <h3>Loading a Template</h3>
      <ol>
        <li>In the Strategy Builder, click <strong>Load Template</strong></li>
        <li>Browse your saved templates</li>
        <li>Click on a template to preview it</li>
        <li>Click <strong>Load</strong> to apply the template to your canvas</li>
      </ol>
      
      <h3>Template Contents</h3>
      <p>Templates save the complete strategy configuration:</p>
      <ul>
        <li><strong>All Nodes:</strong> Node types, positions, and configurations</li>
        <li><strong>Connections:</strong> How nodes are connected together</li>
        <li><strong>Parameters:</strong> All node settings and values</li>
        <li><strong>Layout:</strong> Visual arrangement of nodes on the canvas</li>
      </ul>
      
      <h3>Managing Templates</h3>
      <ul>
        <li><strong>Rename:</strong> Update template name and description</li>
        <li><strong>Delete:</strong> Remove templates you no longer need</li>
        <li><strong>Duplicate:</strong> Create a copy of a template</li>
      </ul>
      
      <h3>Template Isolation</h3>
      <p>Templates are user-scoped - only you can see and use your templates. They are stored securely in Cloudflare KV with your user ID.</p>
    `
  },
  'scheduler': {
    title: 'Scheduler',
    content: `
      <h2>Scheduler</h2>
      <p>The Scheduler automates strategy execution, allowing your strategies to run continuously without manual intervention.</p>
      
      <h3>Starting a Schedule</h3>
      <ol>
        <li>Open your strategy in the Strategy Builder</li>
        <li>Configure all node parameters</li>
        <li>Click <strong>Start Strategy</strong> in the toolbar</li>
        <li>Choose <strong>Paper Trading</strong> or <strong>Live Trading</strong></li>
        <li>Confirm to start the schedule</li>
      </ol>
      
      <h3>Schedule Configuration</h3>
      <p>When starting a strategy, you can configure:</p>
      <ul>
        <li><strong>Trading Mode:</strong> Paper (simulated) or Live (real money)</li>
        <li><strong>Scan Interval:</strong> How often to scan for signals (1m, 5m, 15m, etc.)</li>
        <li><strong>Max Concurrent Positions:</strong> Limit on simultaneous open positions</li>
        <li><strong>Max Signals Per Run:</strong> Limit on signals per scan cycle</li>
        <li><strong>Budget Per Trade:</strong> Amount to allocate per position</li>
      </ul>
      
      <h3>Schedule Execution</h3>
      <p>Once started, the scheduler runs automatically:</p>
      <ul>
        <li><strong>Market Scanning:</strong> Scans configured pairs at the set interval</li>
        <li><strong>Signal Generation:</strong> Evaluates technical indicators and conditions</li>
        <li><strong>Order Execution:</strong> Places orders when signals meet criteria</li>
        <li><strong>Position Management:</strong> Monitors and manages open positions every minute</li>
      </ul>
      
      <h3>Pausing and Stopping</h3>
      <ul>
        <li><strong>Pause:</strong> Temporarily stop scanning without closing positions</li>
        <li><strong>Resume:</strong> Continue scanning after a pause</li>
        <li><strong>Stop:</strong> Completely stop the strategy (positions remain open)</li>
        <li><strong>Stop & Close All:</strong> Stop strategy and close all positions</li>
      </ul>
      
      <h3>Schedule Status</h3>
      <p>View schedule status on the Trading Dashboard:</p>
      <ul>
        <li><strong>Active:</strong> Strategy is running and scanning</li>
        <li><strong>Paused:</strong> Scanning stopped, positions monitored</li>
        <li><strong>Stopped:</strong> Strategy is not running</li>
        <li><strong>Error:</strong> Strategy encountered an error</li>
      </ul>
      
      <h3>Quick Actions</h3>
      <p>The scheduler supports quick actions for rapid adjustments:</p>
      <ul>
        <li><strong>Safe Mode:</strong> Increase confidence threshold, reduce risk</li>
        <li><strong>Anti-Chop:</strong> Stricter filters for choppy markets</li>
        <li><strong>Low Rate Limit:</strong> Reduce scan frequency for API limits</li>
        <li><strong>Max Concurrent Limit:</strong> Reduce maximum positions</li>
      </ul>
    `
  },
  'rescue-system': {
    title: 'Rescue System',
    content: `
      <h2>Rescue System</h2>
      <p>The Rescue System is an advanced scale-in recovery mechanism that helps recover underwater positions by averaging down strategically.</p>
      
      <h3>How It Works</h3>
      <p>When a position goes negative beyond a trigger threshold, the rescue system can:</p>
      <ol>
        <li>Add additional budget to the position (scale-in)</li>
        <li>Lower the average entry price</li>
        <li>Reduce the price movement needed to break even</li>
        <li>Continue adding at deeper levels if needed</li>
      </ol>
      
      <h3>Rescue Configuration</h3>
      <p>Configure rescue parameters in the Risk Manager node:</p>
      <ul>
        <li><strong>Enable Rescue:</strong> Turn the rescue system on/off</li>
        <li><strong>Trigger Threshold:</strong> P&L percentage to trigger first rescue (e.g., -3%)</li>
        <li><strong>Max Rescue Adds:</strong> Maximum number of scale-in additions (1-5)</li>
        <li><strong>Add Multiplier:</strong> Size multiplier for each add (e.g., 1.5x, 2x)</li>
        <li><strong>Level Spacing:</strong> Percentage between rescue levels</li>
        <li><strong>Hard Loss Wall:</strong> Maximum loss before forced close (e.g., -8%)</li>
      </ul>
      
      <h3>Rescue Levels</h3>
      <p>Example with 3 rescue adds at -3% trigger, 2% spacing:</p>
      <ul>
        <li><strong>Level 1:</strong> Triggered at -3%, adds 1.5x original size</li>
        <li><strong>Level 2:</strong> Triggered at -5%, adds 2x original size</li>
        <li><strong>Level 3:</strong> Triggered at -7%, adds 2.5x original size</li>
        <li><strong>Hard Wall:</strong> At -8%, position is force-closed</li>
      </ul>
      
      <h3>Manual Rescue</h3>
      <p>You can also manually trigger rescue operations:</p>
      <ol>
        <li>Go to Trading Dashboard</li>
        <li>Find the underwater position</li>
        <li>Click <strong>Add Budget</strong></li>
        <li>Enter the amount to add</li>
        <li>Confirm the rescue add</li>
      </ol>
      
      <h3>Rescue Events</h3>
      <p>The Strategy Monitor shows rescue events:</p>
      <ul>
        <li><strong>RESCUE_TRIGGERED:</strong> Rescue threshold hit</li>
        <li><strong>RESCUE_ADD:</strong> Budget added to position</li>
        <li><strong>RESCUE_EXHAUSTED:</strong> All rescue adds used</li>
        <li><strong>HARD_LOSS_WALL:</strong> Position hit maximum loss</li>
      </ul>
      
      <h3>Best Practices</h3>
      <ul>
        <li><strong>Don't Over-Rescue:</strong> Limit max adds to 2-3 to avoid excessive exposure</li>
        <li><strong>Set Hard Walls:</strong> Always have a maximum loss limit</li>
        <li><strong>Monitor Closely:</strong> Watch rescued positions carefully</li>
        <li><strong>Paper Test First:</strong> Test rescue settings with paper trading</li>
      </ul>
    `
  },
  'data-source-node': {
    title: 'Data Source Node',
    content: `
      <h2>Data Source Node</h2>
      <p>The Data Source node fetches real-time market data from connected exchanges. It's typically the starting point of any strategy.</p>
      
      <h3>Configuration</h3>
      <ul>
        <li><strong>Exchange:</strong> Select which exchange to fetch data from (Binance, KuCoin, etc.)</li>
        <li><strong>Trading Pairs:</strong> List of symbols to monitor (e.g., BTC-USDT, ETH-USDT)</li>
        <li><strong>Timeframes:</strong> Candlestick timeframes to fetch (1m, 5m, 15m, 1h, 4h, 1d)</li>
        <li><strong>Data Points:</strong> Number of historical candles to retrieve</li>
      </ul>
      
      <h3>Outputs</h3>
      <ul>
        <li><strong>Price Data:</strong> OHLCV (Open, High, Low, Close, Volume) data</li>
        <li><strong>Current Price:</strong> Latest price for each symbol</li>
        <li><strong>Volume:</strong> Trading volume data</li>
      </ul>
      
      <h3>Usage</h3>
      <p>Connect the Data Source output to Technical Analyst or Condition nodes for analysis.</p>
    `
  },
  'technical-analyst-node': {
    title: 'Technical Analyst Node',
    content: `
      <h2>Technical Analyst Node</h2>
      <p>The Technical Analyst node calculates technical indicators and generates trading signals based on price data.</p>
      
      <h3>Configuration</h3>
      
      <h4>Basic Settings</h4>
      <ul>
        <li><strong>Symbol</strong>: The trading pair or asset to analyze (e.g., BTC/USD, AAPL)</li>
        <li><strong>Timeframe</strong>: The chart timeframe to analyze (e.g., 1m, 5m, 15m, 1h, 4h, 1d)</li>
        <li><strong>Indicators</strong>: Technical indicators to include in the analysis</li>
        <li><strong>Assistant ID</strong>: OpenAI Assistant ID for this specific node</li>
      </ul>
      
      <h4>Advanced Settings</h4>
      <ul>
        <li><strong>Analysis Depth</strong>: How detailed the analysis should be</li>
        <li><strong>Update Frequency</strong>: How often to perform new analysis</li>
        <li><strong>Signal Threshold</strong>: Confidence level required to generate signals</li>
        <li><strong>Include Price Action</strong>: Whether to analyze candlestick patterns</li>
      </ul>
      
      <h3>OpenAI Integration</h3>
      <p>The Technical Analyst node leverages OpenAI's capabilities through:</p>
      <ul>
        <li><strong>Assistant Configuration</strong>: Each node uses a dedicated OpenAI Assistant with specific instructions</li>
        <li><strong>Test Connection</strong>: Verify that your OpenAI API key and Assistant ID work properly</li>
        <li><strong>Auto-Save</strong>: Successful connections are automatically saved</li>
      </ul>
      
      <h3>Input/Output</h3>
      
      <h4>Inputs</h4>
      <ul>
        <li><strong>Market Data</strong>: Price and volume data for the specified symbol</li>
        <li><strong>Custom Parameters</strong>: Additional parameters from connected nodes</li>
      </ul>
      
      <h4>Outputs</h4>
      <ul>
        <li><strong>Trading Signals</strong>: Buy, sell, or hold recommendations</li>
        <li><strong>Technical Analysis</strong>: Detailed breakdown of market conditions</li>
        <li><strong>Support/Resistance</strong>: Key price levels identified</li>
        <li><strong>Pattern Recognition</strong>: Identified chart patterns and formations</li>
      </ul>
      
      <h3>Example Configurations</h3>
      
      <h4>Trend Following Analyst</h4>
      <pre><code>{
  "symbol": "BTC/USD",
  "timeframe": "4h",
  "indicators": ["MA(50)", "MA(200)", "MACD", "ADX"],
  "analysisDepth": "detailed",
  "signalThreshold": "high"
}</code></pre>
      
      <h4>Swing Trading Analyst</h4>
      <pre><code>{
  "symbol": "ETH/USD",
  "timeframe": "1d",
  "indicators": ["RSI", "Bollinger Bands", "Stochastic"],
  "includePriceAction": true,
  "signalThreshold": "medium"
}</code></pre>
      
      <h3>Best Practices</h3>
      <ul>
        <li><strong>Create Specific Assistants</strong>: Configure OpenAI Assistants with clear, focused instructions for each analysis type</li>
        <li><strong>Match Timeframes</strong>: Ensure the timeframe matches your trading strategy horizon</li>
        <li><strong>Limit Indicators</strong>: Focus on a few relevant indicators rather than overwhelming with too many</li>
        <li><strong>Connect to Risk Management</strong>: Always feed Technical Analyst outputs to a Risk Manager node</li>
      </ul>
    `
  },
  'condition-node': {
    title: 'Condition Node',
    content: `
      <h2>Condition Node</h2>
      <p>The Condition node evaluates logical conditions and routes signals based on configurable criteria.</p>
      
      <h3>Configuration</h3>
      <ul>
        <li><strong>Condition Type:</strong> Price, indicator, time, or custom condition</li>
        <li><strong>Operator:</strong> Greater than, less than, equals, between, etc.</li>
        <li><strong>Value:</strong> The threshold value to compare against</li>
        <li><strong>Logic:</strong> AND/OR for combining multiple conditions</li>
      </ul>
      
      <h3>Condition Types</h3>
      <ul>
        <li><strong>Price Conditions:</strong> Current price vs threshold</li>
        <li><strong>Indicator Conditions:</strong> RSI > 70, MACD crossover, etc.</li>
        <li><strong>Time Conditions:</strong> Trading hours, day of week</li>
        <li><strong>Volume Conditions:</strong> Volume spike detection</li>
      </ul>
      
      <h3>Outputs</h3>
      <ul>
        <li><strong>True Path:</strong> Signal passes when condition is met</li>
        <li><strong>False Path:</strong> Signal passes when condition is not met</li>
      </ul>
      
      <h3>Usage</h3>
      <p>Use Condition nodes to filter signals or create branching logic in your strategy.</p>
    `
  },
  'risk-manager-node': {
    title: 'Risk Manager Node',
    content: `
      <h2>Risk Manager Node</h2>
      <p>The Risk Manager node controls position sizing, stop-loss, take-profit, and other risk parameters.</p>
      
      <h3>Position Sizing</h3>
      <ul>
        <li><strong>Budget Per Trade:</strong> Fixed amount to allocate per position (e.g., $100)</li>
        <li><strong>Leverage:</strong> Multiplier for position size (1x to 125x)</li>
        <li><strong>Max Concurrent Positions:</strong> Limit on simultaneous open positions</li>
      </ul>
      
      <h3>Stop-Loss Settings</h3>
      <ul>
        <li><strong>Stop-Loss Percent:</strong> Maximum loss before closing (e.g., 1.5%)</li>
        <li><strong>Trailing Stop:</strong> Dynamic stop that follows price (e.g., 0.6%)</li>
        <li><strong>Trailing Activation:</strong> Profit level to activate trailing (e.g., 0.5%)</li>
      </ul>
      
      <h3>Take-Profit Settings</h3>
      <ul>
        <li><strong>Take-Profit Percent:</strong> Target profit to close position (e.g., 2.5%)</li>
        <li><strong>Partial Take-Profit:</strong> Close portion at intermediate levels</li>
      </ul>
      
      <h3>Advanced Risk Controls</h3>
      <ul>
        <li><strong>Zero-Lock:</strong> Move stop to breakeven after reaching profit threshold</li>
        <li><strong>Profit Protection:</strong> Lock in profits with dynamic floor</li>
        <li><strong>Cooldown:</strong> Minimum time between trades on same symbol</li>
      </ul>
      
      <h3>Rescue Settings</h3>
      <ul>
        <li><strong>Enable Rescue:</strong> Turn on scale-in recovery system</li>
        <li><strong>Rescue Trigger:</strong> P&L level to trigger first rescue add</li>
        <li><strong>Max Rescue Adds:</strong> Maximum number of scale-in additions</li>
        <li><strong>Hard Loss Wall:</strong> Maximum loss before forced close</li>
      </ul>
    `
  },
  'signal-matrix-node': {
    title: 'Signal Matrix Node',
    content: `
      <h2>Signal Matrix Node</h2>
      <p>The Signal Matrix node combines multiple technical indicators with configurable weights to generate composite trading signals.</p>
      
      <h3>Indicator Stack</h3>
      <p>Add multiple indicators to your stack:</p>
      <ul>
        <li><strong>EMA Cross:</strong> Exponential moving average crossovers</li>
        <li><strong>RSI:</strong> Relative Strength Index overbought/oversold</li>
        <li><strong>MACD:</strong> Moving Average Convergence Divergence</li>
        <li><strong>Bollinger Bands:</strong> Volatility-based bands</li>
        <li><strong>SuperTrend:</strong> Trend-following indicator</li>
        <li><strong>Stochastic:</strong> Momentum oscillator</li>
        <li><strong>Ichimoku:</strong> Cloud-based trend system</li>
        <li><strong>VWAP:</strong> Volume-weighted average price</li>
        <li><strong>ATR Filter:</strong> Volatility filter</li>
        <li><strong>Support/Resistance:</strong> Key price levels</li>
        <li><strong>Volume Spike:</strong> Unusual volume detection</li>
        <li><strong>Trend Regime:</strong> Market trend classification</li>
      </ul>
      
      <h3>Combine Modes</h3>
      <ul>
        <li><strong>Weighted Score:</strong> Sum of indicator weights, trade if above threshold</li>
        <li><strong>Veto First:</strong> Any indicator can veto the signal</li>
        <li><strong>Consensus:</strong> Majority of indicators must agree</li>
      </ul>
      
      <h3>Configuration</h3>
      <ul>
        <li><strong>Enable/Disable:</strong> Toggle individual indicators</li>
        <li><strong>Weight:</strong> Importance of each indicator (0-100)</li>
        <li><strong>Parameters:</strong> Indicator-specific settings (periods, thresholds)</li>
        <li><strong>Min Score:</strong> Minimum combined score to generate signal</li>
      </ul>
      
      <h3>Output</h3>
      <ul>
        <li><strong>Signal:</strong> BUY, SELL, or HOLD</li>
        <li><strong>Score:</strong> Combined confidence score</li>
        <li><strong>Breakdown:</strong> Individual indicator decisions</li>
      </ul>
    `
  },
  'execution-node': {
    title: 'Execution Node',
    content: `
      <h2>Execution Node</h2>
      <p>The Execution node places trades on connected exchanges based on incoming signals.</p>
      
      <h3>Configuration</h3>
      <ul>
        <li><strong>Exchange:</strong> Which exchange to execute on</li>
        <li><strong>Trading Mode:</strong> Paper (simulated) or Live (real)</li>
        <li><strong>Order Type:</strong> Market or Limit orders</li>
        <li><strong>Confirmation:</strong> Require manual confirmation (optional)</li>
      </ul>
      
      <h3>Order Types</h3>
      <ul>
        <li><strong>Market Order:</strong> Execute immediately at current price</li>
        <li><strong>Limit Order:</strong> Execute at specified price or better</li>
        <li><strong>Stop Order:</strong> Trigger order when price reaches level</li>
      </ul>
      
      <h3>Execution Flow</h3>
      <ol>
        <li>Receive signal from upstream node</li>
        <li>Validate against risk parameters</li>
        <li>Calculate position size</li>
        <li>Place order on exchange</li>
        <li>Create position record in KV</li>
        <li>Emit execution event</li>
      </ol>
      
      <h3>Paper Trading</h3>
      <p>In paper mode:</p>
      <ul>
        <li>Orders are simulated, not sent to exchange</li>
        <li>Uses real market prices</li>
        <li>Tracks virtual P&L</li>
        <li>Perfect for testing strategies</li>
      </ul>
      
      <h3>Live Trading</h3>
      <p>In live mode:</p>
      <ul>
        <li>Real orders sent to exchange</li>
        <li>Real money at risk</li>
        <li>Requires funded exchange account</li>
        <li>Requires valid API credentials</li>
      </ul>
    `
  },
  'rescue-manager-node': {
    title: 'Rescue Manager Node',
    content: `
      <h2>Rescue Manager Node</h2>
      <p>The Rescue Manager node handles scale-in recovery for underwater positions.</p>
      
      <h3>How It Works</h3>
      <p>When a position goes negative beyond the trigger threshold:</p>
      <ol>
        <li>Evaluates if rescue conditions are met</li>
        <li>Calculates optimal add size</li>
        <li>Places rescue order to average down</li>
        <li>Updates position with new average entry</li>
        <li>Continues monitoring for additional rescue levels</li>
      </ol>
      
      <h3>Configuration</h3>
      <ul>
        <li><strong>Trigger Threshold:</strong> P&L level to trigger rescue (e.g., -3%)</li>
        <li><strong>Max Adds:</strong> Maximum number of rescue additions (1-5)</li>
        <li><strong>Add Multiplier:</strong> Size multiplier for each add</li>
        <li><strong>Level Spacing:</strong> Percentage between rescue levels</li>
        <li><strong>Hard Loss Wall:</strong> Maximum loss before forced close</li>
      </ul>
      
      <h3>Rescue Levels Example</h3>
      <p>With 3 max adds, -3% trigger, 2% spacing:</p>
      <ul>
        <li><strong>Level 1:</strong> -3% → Add 1.5x original size</li>
        <li><strong>Level 2:</strong> -5% → Add 2x original size</li>
        <li><strong>Level 3:</strong> -7% → Add 2.5x original size</li>
        <li><strong>Hard Wall:</strong> -8% → Force close</li>
      </ul>
      
      <h3>Safety Features</h3>
      <ul>
        <li><strong>Budget Check:</strong> Verifies sufficient funds before adding</li>
        <li><strong>Hard Loss Wall:</strong> Prevents unlimited losses</li>
        <li><strong>Exhaustion Handling:</strong> Proper handling when all adds used</li>
      </ul>
    `
  },
  'report-synthesizer-node': {
    title: 'Report Synthesizer Node',
    content: `
      <h2>Report Synthesizer Node</h2>
      <p>The Report Synthesizer node sends trading notifications and reports via Telegram.</p>
      
      <h3>Configuration</h3>
      <ul>
        <li><strong>Telegram Bot Token:</strong> Your Telegram bot's API token</li>
        <li><strong>Chat ID:</strong> Target chat/channel ID for messages</li>
        <li><strong>Notification Types:</strong> Which events to report</li>
      </ul>
      
      <h3>Notification Types</h3>
      <ul>
        <li><strong>Trade Opened:</strong> New position entry notification</li>
        <li><strong>Trade Closed:</strong> Position exit with P&L</li>
        <li><strong>Stop Loss Hit:</strong> Position closed at stop loss</li>
        <li><strong>Take Profit Hit:</strong> Position closed at take profit</li>
        <li><strong>Rescue Triggered:</strong> Scale-in rescue activated</li>
        <li><strong>Daily Summary:</strong> End-of-day performance report</li>
      </ul>
      
      <h3>Message Format</h3>
      <p>Notifications include:</p>
      <ul>
        <li>Symbol and direction (LONG/SHORT)</li>
        <li>Entry/exit price</li>
        <li>Position size and leverage</li>
        <li>P&L amount and percentage</li>
        <li>Strategy name</li>
        <li>Close reason</li>
      </ul>
      
      <h3>Daily Summary</h3>
      <p>End-of-day report includes:</p>
      <ul>
        <li>Total trades executed</li>
        <li>Win/loss count</li>
        <li>Total P&L</li>
        <li>Best and worst trades</li>
        <li>Active positions summary</li>
      </ul>
    `
  },
  'architecture': {
    title: 'System Architecture',
    content: `
      <h2>System Architecture</h2>
      <p>The AI Trading System is built entirely on Cloudflare's edge infrastructure, providing low-latency, globally distributed execution with automatic scaling.</p>
      
      <h3>Cloudflare Stack</h3>
      
      <h4>Frontend (Cloudflare Pages)</h4>
      <ul>
        <li><strong>React SPA:</strong> Single-page application with Material-UI components</li>
        <li><strong>Strategy Builder:</strong> Interactive node-based canvas using React Flow</li>
        <li><strong>Real-time Updates:</strong> Server-Sent Events (SSE) for live monitoring</li>
        <li><strong>State Management:</strong> React Context API with JWT authentication</li>
        <li><strong>Global CDN:</strong> Static assets served from 300+ edge locations</li>
      </ul>
      
      <h4>Backend (Cloudflare Workers)</h4>
      <ul>
        <li><strong>Strategy Engine:</strong> TypeScript-based strategy execution engine</li>
        <li><strong>REST API:</strong> Hono framework for API routing</li>
        <li><strong>Cron Triggers:</strong> Scheduled tasks for market scanning and position management</li>
        <li><strong>Edge Execution:</strong> Code runs in 300+ data centers worldwide</li>
      </ul>
      
      <h4>Data Storage</h4>
      <ul>
        <li><strong>D1 (SQLite):</strong> Relational database for users, strategies, runs, orders, positions</li>
        <li><strong>KV (Key-Value):</strong> Real-time state storage for positions, schedules, API connections</li>
        <li><strong>R2 (Object Storage):</strong> Strategy templates, backups, and large data objects</li>
      </ul>
      
      <h3>Data Flow</h3>
      
      <h4>Strategy Execution Flow</h4>
      <ol>
        <li>User creates strategy in Strategy Builder → saved to D1 + KV</li>
        <li>User starts strategy → creates schedule in KV</li>
        <li>Cron trigger fires every minute → loads active schedules from KV</li>
        <li>Strategy Engine scans markets via exchange APIs</li>
        <li>Signals generated → evaluated against risk parameters</li>
        <li>Orders placed via exchange APIs (paper or live)</li>
        <li>Positions stored in KV with real-time P&L updates</li>
        <li>Events streamed to frontend via SSE</li>
      </ol>
      
      <h4>Position Management Loop</h4>
      <ol>
        <li>Every minute: Check all open positions</li>
        <li>Fetch current prices from exchanges</li>
        <li>Evaluate stop-loss, take-profit, trailing stops</li>
        <li>Execute rescue scale-ins if triggered</li>
        <li>Close positions that hit exit criteria</li>
        <li>Update KV state and emit events</li>
      </ol>
      
      <h3>Security Architecture</h3>
      <ul>
        <li><strong>JWT Authentication:</strong> HMAC-SHA256 signed tokens with 1-hour expiry</li>
        <li><strong>Password Hashing:</strong> PBKDF2 with Web Crypto API</li>
        <li><strong>Multi-Tenant Isolation:</strong> All data scoped by user_id</li>
        <li><strong>API Key Encryption:</strong> Exchange credentials encrypted at rest</li>
        <li><strong>CORS Protection:</strong> Strict origin validation</li>
        <li><strong>Audit Logging:</strong> All actions logged with user context</li>
      </ul>
      
      <h3>Scalability</h3>
      <ul>
        <li><strong>Auto-Scaling:</strong> Cloudflare Workers scale automatically to handle load</li>
        <li><strong>Global Distribution:</strong> Requests routed to nearest edge location</li>
        <li><strong>No Cold Starts:</strong> Workers are always warm and ready</li>
        <li><strong>Rate Limiting:</strong> Built-in protection against abuse</li>
      </ul>
    `
  },
  'cloudflare-stack': {
    title: 'Cloudflare Stack',
    content: `
      <h2>Cloudflare Stack Details</h2>
      <p>Deep dive into the Cloudflare services powering the AI Trading System.</p>
      
      <h3>Cloudflare Workers</h3>
      <p>The strategy engine runs as a Cloudflare Worker:</p>
      <ul>
        <li><strong>Runtime:</strong> V8 JavaScript engine (same as Chrome)</li>
        <li><strong>Language:</strong> TypeScript compiled to JavaScript</li>
        <li><strong>Framework:</strong> Hono for routing and middleware</li>
        <li><strong>Execution Limit:</strong> 30 seconds CPU time per request</li>
        <li><strong>Memory:</strong> 128MB per request</li>
      </ul>
      
      <h3>Cloudflare D1</h3>
      <p>SQLite database at the edge:</p>
      <ul>
        <li><strong>Tables:</strong> users, strategies, runs, orders, trades, positions, schedules</li>
        <li><strong>Migrations:</strong> SQL migration files for schema changes</li>
        <li><strong>Queries:</strong> Standard SQL with prepared statements</li>
        <li><strong>Replication:</strong> Automatic global replication</li>
      </ul>
      
      <h3>Cloudflare KV</h3>
      <p>Key-value storage for real-time state:</p>
      <ul>
        <li><strong>Position State:</strong> <code>positions:{runId}</code> - current position data</li>
        <li><strong>Schedules:</strong> <code>schedule:{strategyId}</code> - active schedule configs</li>
        <li><strong>API Connections:</strong> <code>api_connection:{userId}:{type}</code> - exchange credentials</li>
        <li><strong>Templates:</strong> <code>template:{userId}:{templateId}</code> - saved strategy templates</li>
        <li><strong>Events:</strong> <code>events:{strategyId}</code> - buffered SSE events</li>
      </ul>
      
      <h3>Cloudflare R2</h3>
      <p>S3-compatible object storage:</p>
      <ul>
        <li><strong>Strategy Backups:</strong> Full strategy JSON exports</li>
        <li><strong>Template Library:</strong> Shared strategy templates</li>
        <li><strong>Historical Data:</strong> Archived trade history</li>
      </ul>
      
      <h3>Cron Triggers</h3>
      <p>Scheduled tasks for automated execution:</p>
      <ul>
        <li><strong>Every Minute:</strong> Position management, price updates, SL/TP checks</li>
        <li><strong>Every 5 Minutes:</strong> Market scanning, signal generation</li>
        <li><strong>Daily:</strong> Performance reports, cleanup tasks</li>
      </ul>
      
      <h3>Deployment</h3>
      <p>Deployment is handled via Wrangler CLI:</p>
      <pre><code># Deploy frontend to Pages
npx wrangler pages deploy build --project-name=ai-trading-system-3

# Deploy worker
npx wrangler deploy

# Run D1 migrations
npx wrangler d1 migrations apply ai-trading-db</code></pre>
    `
  },
  'custom-nodes': {
    title: 'Creating Custom Nodes',
    content: `
      <h2>Creating Custom Nodes</h2>
      <p>The AI Trading System allows advanced users to create custom nodes that extend the platform's capabilities.</p>
      
      <h3>Node Development Process</h3>
      
      <h4>Planning Your Node</h4>
      <ol>
        <li><strong>Define Purpose</strong>: Determine what your node will do</li>
        <li><strong>Identify Inputs/Outputs</strong>: What data will it consume and produce?</li>
        <li><strong>Design Parameters</strong>: What settings will users need to configure?</li>
        <li><strong>Plan UI</strong>: How will your node appear in the Strategy Builder?</li>
      </ol>
      
      <h4>Creating Node Components</h4>
      <p>Each custom node requires several components:</p>
      <ul>
        <li><strong>Node Definition</strong>: JSON schema defining the node's properties</li>
        <li><strong>Node Component</strong>: React component for the node's appearance</li>
        <li><strong>Node Logic</strong>: JavaScript module containing business logic</li>
        <li><strong>Settings Panel</strong>: Component for configuring node parameters</li>
      </ul>
      
      <h3>Node Definition Example</h3>
      <pre><code>// myCustomNode.js
export const myCustomNodeDefinition = {
  type: 'myCustomNode',
  label: 'My Custom Node',
  description: 'A custom node that does something interesting',
  category: 'custom',
  inputs: [
    { id: 'dataIn', label: 'Data Input' }
  ],
  outputs: [
    { id: 'dataOut', label: 'Processed Data' }
  ],
  defaultParams: {
    threshold: 0.5,
    enableFeatureX: true
  },
  icon: CustomNodeIcon
};</code></pre>
      
      <h3>Node Logic Implementation</h3>
      <pre><code>// myCustomNodeLogic.js
export function processData(inputs, params) {
  const { dataIn } = inputs;
  const { threshold, enableFeatureX } = params;
  
  // Your custom logic here
  let processedData = dataIn.map(item => {
    // Transform data according to your algorithm
    return {
      ...item,
      signal: item.value > threshold ? 'BUY' : 'SELL'
    };
  });
  
  if (enableFeatureX) {
    // Additional processing for feature X
    processedData = applyFeatureX(processedData);
  }
  
  return {
    dataOut: processedData
  };
}</code></pre>
      
      <h3>Node UI Component</h3>
      <pre><code>// MyCustomNodeComponent.jsx
import React from 'react';
import { Handle } from 'react-flow-renderer';

function MyCustomNodeComponent({ data }) {
  return (
    <div className="custom-node">
      <Handle
        type="target"
        position="left"
        id="dataIn"
        style={{ background: '#555' }}
      />
      <div>
        <div className="custom-node-header">
          <span>{data.label || 'My Custom Node'}</span>
        </div>
        <div className="custom-node-content">
          <p>Threshold: {data.params.threshold}</p>
          <p>Feature X: {data.params.enableFeatureX ? 'Enabled' : 'Disabled'}</p>
        </div>
      </div>
      <Handle
        type="source"
        position="right"
        id="dataOut"
        style={{ background: '#555' }}
      />
    </div>
  );
}

export default MyCustomNodeComponent;</code></pre>
      
      <h3>Registering Your Custom Node</h3>
      <p>Once you've created all the necessary components, register your node with the system:</p>
      <pre><code>// In your node registry file
import { myCustomNodeDefinition } from './myCustomNode';
import MyCustomNodeComponent from './MyCustomNodeComponent';
import { processData } from './myCustomNodeLogic';

function registerCustomNodes(nodeRegistry) {
  nodeRegistry.registerNode({
    definition: myCustomNodeDefinition,
    component: MyCustomNodeComponent,
    logic: processData
  });
}</code></pre>
      
      <h3>Node Testing</h3>
      <p>Before deploying your custom node:</p>
      <ol>
        <li>Create unit tests for your node logic</li>
        <li>Test the node in isolation with sample inputs</li>
        <li>Test the node in a complete strategy flow</li>
        <li>Verify the UI renders correctly</li>
      </ol>
    `
  },
  'common-issues': {
    title: 'Common Issues',
    content: `
      <h2>Common Issues and Troubleshooting</h2>
      <p>This section covers frequently encountered issues and their solutions.</p>
      
      <h3>API Connection Issues</h3>
      
      <h4>Broker Connection Failures</h4>
      <ul>
        <li><strong>Issue</strong>: Unable to connect to broker API</li>
        <li><strong>Solutions</strong>:
          <ul>
            <li>Verify your API key and secret are entered correctly</li>
            <li>Check that your API key has trading permissions enabled</li>
            <li>Ensure your IP address isn't blocked by the broker</li>
            <li>Verify the broker's API servers are operational</li>
            <li>Check for API endpoint changes in broker documentation</li>
          </ul>
        </li>
      </ul>
      
      <h4>OpenAI API Issues</h4>
      <ul>
        <li><strong>Issue</strong>: OpenAI API calls failing or timing out</li>
        <li><strong>Solutions</strong>:
          <ul>
            <li>Check your OpenAI API key is valid and has sufficient credits</li>
            <li>Verify the Assistant IDs exist in your OpenAI account</li>
            <li>Reduce the complexity of requests if hitting rate limits</li>
            <li>Check OpenAI's status page for service disruptions</li>
          </ul>
        </li>
      </ul>
      
      <h3>Strategy Execution Problems</h3>
      
      <h4>Strategies Not Starting</h4>
      <ul>
        <li><strong>Issue</strong>: Strategy won't start when activated</li>
        <li><strong>Solutions</strong>:
          <ul>
            <li>Check for missing connections between nodes</li>
            <li>Verify all required node parameters are set</li>
            <li>Ensure broker connections are active</li>
            <li>Check system logs for specific error messages</li>
          </ul>
        </li>
      </ul>
      
      <h4>No Trading Signals Generated</h4>
      <ul>
        <li><strong>Issue</strong>: Strategy running but not producing signals</li>
        <li><strong>Solutions</strong>:
          <ul>
            <li>Verify market data is being received</li>
            <li>Check signal thresholds aren't set too high</li>
            <li>Ensure technical indicators have enough data to calculate</li>
            <li>Check that the symbol/asset is available and active</li>
            <li>Verify OpenAI connections are working properly</li>
          </ul>
        </li>
      </ul>
      
      <h3>Performance Issues</h3>
      
      <h4>UI Responsiveness</h4>
      <ul>
        <li><strong>Issue</strong>: Strategy Builder becomes slow with many nodes</li>
        <li><strong>Solutions</strong>:
          <ul>
            <li>Simplify complex strategies into sub-components</li>
            <li>Close unused tabs and windows</li>
            <li>Clear browser cache and reload</li>
            <li>Ensure your device meets minimum system requirements</li>
          </ul>
        </li>
      </ul>
      
      <h4>Memory Usage</h4>
      <ul>
        <li><strong>Issue</strong>: System consuming excessive memory</li>
        <li><strong>Solutions</strong>:
          <ul>
            <li>Limit the number of concurrent running strategies</li>
            <li>Reduce the timeframes of data being processed</li>
            <li>Close and restart the application if running for extended periods</li>
            <li>Check for memory leaks in custom nodes</li>
          </ul>
        </li>
      </ul>
      
      <h3>Data Synchronization</h3>
      
      <h4>Missing Historical Data</h4>
      <ul>
        <li><strong>Issue</strong>: Historical data missing for backtesting</li>
        <li><strong>Solutions</strong>:
          <ul>
            <li>Manually trigger data sync for the specific symbol and timeframe</li>
            <li>Check data provider API status and limits</li>
            <li>Verify the date range is available from your data source</li>
            <li>Import data from alternative sources if available</li>
          </ul>
        </li>
      </ul>
      
      <h4>Real-time Data Delays</h4>
      <ul>
        <li><strong>Issue</strong>: Real-time data feed experiencing delays</li>
        <li><strong>Solutions</strong>:
          <ul>
            <li>Check your internet connection stability</li>
            <li>Verify websocket connections are active</li>
            <li>Check if you've exceeded API rate limits</li>
            <li>Restart the data service component</li>
          </ul>
        </li>
      </ul>
      
      <h3>Getting Support</h3>
      <p>If you continue to experience issues after trying these solutions:</p>
      <ul>
        <li>Check the <a href="#">community forums</a> for similar issues and solutions</li>
        <li>Review detailed logs in the system's log directory</li>
        <li>Submit a support ticket with detailed information about your issue</li>
        <li>For enterprise users, contact your dedicated support representative</li>
      </ul>
    `
  },
  'api-documentation': {
    title: 'API Documentation',
    content: `
      <h2>API Documentation</h2>
      <p>The AI Trading System provides a comprehensive API that allows developers to integrate with the platform and extend its functionality.</p>
      
      <h3>API Overview</h3>
      <p>The system exposes RESTful API endpoints for:</p>
      <ul>
        <li><strong>Data Access</strong>: Retrieve market data, account information, and trading history</li>
        <li><strong>Strategy Management</strong>: Create, read, update, and delete trading strategies</li>
        <li><strong>Trade Execution</strong>: Place, modify, and cancel orders</li>
        <li><strong>Account Management</strong>: Configure integrations and system settings</li>
      </ul>
      
      <h3>Authentication</h3>
      <p>All API requests require authentication using:</p>
      <ul>
        <li><strong>API Key</strong>: Unique identifier for your application</li>
        <li><strong>Secret Key</strong>: Private key used to sign requests</li>
        <li><strong>JWT Tokens</strong>: JSON Web Tokens for session management</li>
      </ul>
      <p>Request Example:</p>
      <pre><code>curl -X GET https://api.aitradingsystem.com/v1/strategies \\n  -H "Authorization: Bearer YOUR_JWT_TOKEN" \\n  -H "Content-Type: application/json"</code></pre>
      
      <h3>Core Endpoints</h3>
      
      <h4>Strategy Endpoints</h4>
      <ul>
        <li><code>GET /v1/strategies</code> - List all strategies</li>
        <li><code>GET /v1/strategies/{id}</code> - Get strategy details</li>
        <li><code>POST /v1/strategies</code> - Create new strategy</li>
        <li><code>PUT /v1/strategies/{id}</code> - Update strategy</li>
        <li><code>DELETE /v1/strategies/{id}</code> - Delete strategy</li>
        <li><code>POST /v1/strategies/{id}/start</code> - Start strategy</li>
        <li><code>POST /v1/strategies/{id}/stop</code> - Stop strategy</li>
      </ul>
      
      <h4>Trading Endpoints</h4>
      <ul>
        <li><code>GET /v1/positions</code> - List all open positions</li>
        <li><code>GET /v1/orders</code> - List all orders</li>
        <li><code>POST /v1/orders</code> - Place new order</li>
        <li><code>PUT /v1/orders/{id}</code> - Modify order</li>
        <li><code>DELETE /v1/orders/{id}</code> - Cancel order</li>
      </ul>
      
      <h4>Data Endpoints</h4>
      <ul>
        <li><code>GET /v1/market-data/{symbol}</code> - Get market data for symbol</li>
        <li><code>GET /v1/history/{symbol}</code> - Get historical price data</li>
        <li><code>GET /v1/indicators/{symbol}</code> - Get technical indicator values</li>
      </ul>
      
      <h3>Webhooks</h3>
      <p>The system can send webhook notifications for various events:</p>
      <ul>
        <li><strong>Order Events</strong>: Order placed, filled, modified, canceled</li>
        <li><strong>Trade Signals</strong>: New trade signals from strategies</li>
        <li><strong>System Alerts</strong>: Connection issues, balance thresholds, etc.</li>
      </ul>
      <p>Webhook payloads are sent as JSON to your configured endpoint.</p>
      
      <h3>Rate Limits</h3>
      <p>To ensure system stability, API requests are subject to rate limits:</p>
      <ul>
        <li><strong>Standard Tier</strong>: 120 requests per minute</li>
        <li><strong>Premium Tier</strong>: 600 requests per minute</li>
        <li><strong>Enterprise Tier</strong>: Custom limits available</li>
      </ul>
      <p>Rate limit headers are included in all API responses.</p>
      
      <h3>Error Handling</h3>
      <p>API errors follow standard HTTP status codes with detailed error messages:</p>
      <pre><code>{
  "error": {
    "code": "invalid_order",
    "message": "Order price must be greater than zero",
    "status": 400
  }
}</code></pre>
      
      <h3>SDK Libraries</h3>
      <p>Official SDK libraries are available for:</p>
      <ul>
        <li><strong>Python</strong>: <code>pip install aitradingsystem</code></li>
        <li><strong>JavaScript</strong>: <code>npm install aitradingsystem-js</code></li>
        <li><strong>Go</strong>: <code>go get github.com/aitradingsystem/go-client</code></li>
      </ul>
    `
  },
  'creating-strategies': {
    title: 'Creating Trading Strategies',
    content: `
      <h2>Creating Trading Strategies</h2>
      <p>Learn how to build effective trading strategies using the Strategy Builder interface.</p>
      
      <h3>Basic Strategy Structure</h3>
      <p>Every strategy should include these essential components:</p>
      <ol>
        <li><strong>Data Input:</strong> Market data, news feeds, or other information sources</li>
        <li><strong>Analysis:</strong> Technical or fundamental analysis to process the input data</li>
        <li><strong>Signal Generation:</strong> Logic to identify trading opportunities</li>
        <li><strong>Risk Management:</strong> Rules to manage position sizing and risk</li>
        <li><strong>Execution:</strong> Logic to execute trades with a broker</li>
      </ol>
      
      <h3>Building Your First Strategy</h3>
      <p>Start with a template or blank canvas, add nodes, connect them logically, and configure each node's parameters.</p>
      
      <h3>Advanced Strategy Techniques</h3>
      <ul>
        <li><strong>Multi-timeframe Analysis:</strong> Use multiple Technical Analyst nodes with different timeframes</li>
        <li><strong>Hybrid Analysis:</strong> Combine technical and fundamental analysis nodes</li>
        <li><strong>Conditional Execution:</strong> Create different paths based on market conditions</li>
      </ul>
    `
  },
  'openai-integration': {
    title: 'OpenAI Integration',
    content: `
      <h2>OpenAI Integration</h2>
      <p>Learn how to leverage OpenAI's capabilities to enhance your trading strategies.</p>
      
      <h3>Setting Up OpenAI</h3>
      <ol>
        <li>Obtain an API key from OpenAI</li>
        <li>In the AI Trading System, go to Settings → API Keys</li>
        <li>Enter your OpenAI API key</li>
        <li>Click "Test Connection" to verify it works</li>
      </ol>
      
      <h3>Configuring Assistant IDs</h3>
      <p>Each AI node requires its own OpenAI Assistant ID:</p>
      <ol>
        <li>Create assistants in the OpenAI platform with appropriate instructions</li>
        <li>Copy the Assistant IDs from your OpenAI dashboard</li>
        <li>In each AI node, enter the corresponding Assistant ID</li>
        <li>Use the "Test Connection" button to verify the assistant works</li>
      </ol>
      
      <h3>Effective Prompt Engineering</h3>
      <p>Tips for creating effective prompts for your trading assistants:</p>
      <ul>
        <li><strong>Be Specific:</strong> Clearly define what indicators or patterns you want analyzed</li>
        <li><strong>Include Reasoning:</strong> Ask the assistant to explain its analysis process</li>
        <li><strong>Structure Output:</strong> Request a specific format for signals (e.g., BUY/SELL/HOLD)</li>
      </ul>
    `
  },
  'introduction': {
    title: 'Introduction to AI Trading System',
    content: `
      <h2>Welcome to the AI Trading System</h2>
      <p>The AI Trading System is a comprehensive, cloud-native trading platform designed for automated cryptocurrency and stock trading. Built on Cloudflare's edge infrastructure, it provides low-latency execution, real-time monitoring, and sophisticated risk management tools.</p>
      
      <h3>Key Features</h3>
      <ul>
        <li><strong>Visual Strategy Builder:</strong> Create complex trading strategies with a drag-and-drop node-based interface - no coding required</li>
        <li><strong>Multi-Exchange Support:</strong> Connect to Binance, KuCoin, Bybit, OKX, and Alpaca simultaneously</li>
        <li><strong>Real-Time Strategy Monitor:</strong> Watch your strategies execute with live Server-Sent Events (SSE) streaming</li>
        <li><strong>Advanced Risk Management:</strong> Stop-loss, take-profit, trailing stops, and position sizing controls</li>
        <li><strong>Rescue System:</strong> Automatic scale-in recovery for underwater positions</li>
        <li><strong>Scheduler:</strong> Automate strategy execution with flexible scheduling options</li>
        <li><strong>Paper Trading:</strong> Test strategies with simulated trades before going live</li>
        <li><strong>Multi-Tenant Architecture:</strong> Secure, isolated data for each user account</li>
        <li><strong>Telegram Reporting:</strong> Automated trade notifications and daily summaries</li>
        <li><strong>Performance Analytics:</strong> Comprehensive trade history with P&L tracking and strategy breakdowns</li>
      </ul>
      
      <h3>Platform Architecture</h3>
      <p>The platform runs entirely on Cloudflare's edge network:</p>
      <ul>
        <li><strong>Frontend:</strong> React SPA hosted on Cloudflare Pages</li>
        <li><strong>Backend:</strong> Cloudflare Workers for serverless API and strategy execution</li>
        <li><strong>Database:</strong> Cloudflare D1 (SQLite) for structured data</li>
        <li><strong>Cache/State:</strong> Cloudflare KV for real-time position and strategy state</li>
        <li><strong>Storage:</strong> Cloudflare R2 for templates and backups</li>
      </ul>
      
      <p>This documentation will guide you through all aspects of the platform, from account setup to advanced strategy development.</p>
    `
  },
  'account-setup': {
    title: 'Account Setup',
    content: `
      <h2>Account Setup</h2>
      <p>Get started with the AI Trading System by creating your account and configuring your trading environment.</p>
      
      <h3>Creating Your Account</h3>
      <ol>
        <li>Navigate to the platform at <a href="https://ai-trading-system-3.pages.dev" target="_blank">ai-trading-system-3.pages.dev</a></li>
        <li>Click <strong>Register</strong> on the login page</li>
        <li>Enter your email address and create a secure password</li>
        <li>Complete the registration form with your display name</li>
        <li>Click <strong>Create Account</strong> to register</li>
      </ol>
      
      <h3>First Login</h3>
      <ol>
        <li>Enter your email and password on the login page</li>
        <li>You'll be redirected to your personal Trading Dashboard</li>
        <li>All your data (strategies, positions, settings) is isolated to your account</li>
      </ol>
      
      <h3>Configuring Exchange API Keys</h3>
      <p>To start trading, you need to connect at least one exchange:</p>
      <ol>
        <li>Go to <strong>Settings → API Connections</strong></li>
        <li>Select your exchange (Binance, KuCoin, Bybit, OKX, or Alpaca)</li>
        <li>Enter your API Key and Secret Key</li>
        <li>For KuCoin, also enter your Passphrase</li>
        <li>Click <strong>Test Connection</strong> to verify credentials</li>
        <li>Save your configuration</li>
      </ol>
      
      <h3>Security Best Practices</h3>
      <ul>
        <li><strong>API Key Permissions:</strong> Only enable trading permissions you need. Disable withdrawal permissions.</li>
        <li><strong>IP Whitelisting:</strong> If your exchange supports it, whitelist the Cloudflare Worker IPs</li>
        <li><strong>Paper Trading First:</strong> Always test strategies with paper trading before using real funds</li>
        <li><strong>Start Small:</strong> Begin with small position sizes until you're confident in your strategy</li>
      </ul>
      
      <h3>Multi-Tenant Data Isolation</h3>
      <p>The platform ensures complete data isolation between users:</p>
      <ul>
        <li>Your strategies are only visible to you</li>
        <li>Your positions and trade history are private</li>
        <li>Your API keys are encrypted and isolated</li>
        <li>Support tickets are only accessible by you and admins</li>
      </ul>
    `
  },
  'nodes-overview': {
    title: 'Strategy Builder Nodes Overview',
    content: `
      <h2>Strategy Builder Nodes</h2>
      <p>The Strategy Builder uses a visual node-based system to create trading strategies. Drag nodes from the library, connect them together, and configure parameters to build your trading logic.</p>
      
      <h3>Node Categories</h3>
      
      <h4>Data Nodes</h4>
      <ul>
        <li><strong>Data Source:</strong> Fetches real-time market data from connected exchanges</li>
        <li><strong>Technical Analyst:</strong> Calculates technical indicators (RSI, MACD, Bollinger Bands, etc.)</li>
      </ul>
      
      <h4>Logic Nodes</h4>
      <ul>
        <li><strong>Condition:</strong> Evaluates conditions and routes signals based on criteria</li>
        <li><strong>Signal Matrix:</strong> Combines multiple signals with configurable weights and thresholds</li>
      </ul>
      
      <h4>Risk Nodes</h4>
      <ul>
        <li><strong>Risk Manager:</strong> Controls position sizing, stop-loss, take-profit, and leverage</li>
        <li><strong>Rescue Manager:</strong> Manages scale-in recovery for underwater positions</li>
      </ul>
      
      <h4>Action Nodes</h4>
      <ul>
        <li><strong>Execution:</strong> Places trades on connected exchanges (paper or live)</li>
        <li><strong>Report Synthesizer:</strong> Sends notifications via Telegram</li>
        <li><strong>Scheduler:</strong> Automates strategy execution on a schedule</li>
      </ul>
      
      <h3>Node Connections</h3>
      <p>Nodes connect via input/output handles:</p>
      <ul>
        <li><strong>Left handles:</strong> Inputs - receive data from other nodes</li>
        <li><strong>Right handles:</strong> Outputs - send data to other nodes</li>
        <li><strong>Color coding:</strong> Different colors indicate data types (green=numbers, blue=signals, etc.)</li>
      </ul>
      
      <h3>Node Configuration</h3>
      <p>Click any node to open its settings panel on the right side. Each node has specific parameters:</p>
      <ul>
        <li><strong>Basic Settings:</strong> Name, description, enabled/disabled</li>
        <li><strong>Node-Specific Settings:</strong> Parameters unique to each node type</li>
        <li><strong>Advanced Settings:</strong> Fine-tuning options for experienced users</li>
      </ul>
      
      <h3>Quick Actions</h3>
      <p>Right-click any node for quick actions:</p>
      <ul>
        <li>Duplicate node</li>
        <li>Delete node</li>
        <li>Disconnect all connections</li>
        <li>Copy/paste configuration</li>
      </ul>
    `
  },
  'alpaca': {
    title: 'Alpaca Integration',
    content: `
      <h2>Alpaca Integration</h2>
      <p>Alpaca is a commission-free API-first broker that offers stocks, ETFs, and crypto trading.</p>
      
      <h3>Setting Up Alpaca</h3>
      <ol>
        <li>Create an account at <a href="https://alpaca.markets/" target="_blank">Alpaca Markets</a></li>
        <li>Navigate to "Paper Trading" if you want to test without real money</li>
        <li>Go to API Keys in your Dashboard</li>
        <li>Generate a new API key pair</li>
        <li>Copy the API Key ID and Secret Key</li>
      </ol>
      
      <h3>Configuration</h3>
      <ol>
        <li>Go to Settings → API Connections → Alpaca</li>
        <li>Enter your API Key and Secret Key</li>
        <li>Toggle "Use Paper Trading" if testing</li>
        <li>Click "Test Connection" to verify</li>
      </ol>
      
      <h3>Available Features</h3>
      <ul>
        <li>US Stocks and ETFs trading</li>
        <li>Crypto trading (selected pairs)</li>
        <li>Market data access</li>
        <li>Fractional shares</li>
      </ul>
    `
  },
  'binance': {
    title: 'Binance Integration',
    content: `
      <h2>Binance Integration</h2>
      <p>Binance is the world's largest cryptocurrency exchange by trading volume. The AI Trading System supports both Binance Spot and Futures trading.</p>
      
      <h3>Setting Up Binance API</h3>
      <ol>
        <li>Log in to your Binance account</li>
        <li>Go to <strong>Account → API Management</strong></li>
        <li>Click <strong>Create API</strong></li>
        <li>Complete security verification</li>
        <li>Copy your API Key and Secret Key</li>
      </ol>
      
      <h3>API Permissions</h3>
      <p>Configure these permissions for your API key:</p>
      <ul>
        <li><strong>Enable Reading:</strong> Required for market data</li>
        <li><strong>Enable Spot & Margin Trading:</strong> Required for spot trading</li>
        <li><strong>Enable Futures:</strong> Required for futures trading</li>
        <li><strong>Disable Withdrawals:</strong> Recommended for security</li>
      </ul>
      
      <h3>IP Restrictions</h3>
      <p>For enhanced security, you can restrict API access to specific IPs. Note that Cloudflare Workers use dynamic IPs, so you may need to allow unrestricted access or use Binance's trusted IP feature.</p>
      
      <h3>Configuration in AI Trading System</h3>
      <ol>
        <li>Go to Settings → API Connections → Binance</li>
        <li>Enter your API Key and Secret Key</li>
        <li>Select trading type (Spot or Futures)</li>
        <li>Click "Test Connection" to verify</li>
      </ol>
      
      <h3>Supported Features</h3>
      <ul>
        <li><strong>Spot Trading:</strong> Buy/sell crypto with USDT pairs</li>
        <li><strong>Futures Trading:</strong> Leveraged trading up to 125x</li>
        <li><strong>Market Data:</strong> Real-time prices, order books, trades</li>
        <li><strong>Order Types:</strong> Market, limit, stop-loss, take-profit</li>
      </ul>
      
      <h3>Rate Limits</h3>
      <p>Binance enforces API rate limits:</p>
      <ul>
        <li><strong>Request Weight:</strong> 1200 per minute</li>
        <li><strong>Order Limit:</strong> 10 orders per second</li>
        <li><strong>24h Order Limit:</strong> 200,000 orders</li>
      </ul>
      <p>The platform automatically manages rate limits to avoid bans.</p>
      
      <h3>Troubleshooting</h3>
      <ul>
        <li><strong>Invalid API Key:</strong> Verify key is copied correctly without spaces</li>
        <li><strong>Permission Denied:</strong> Check API permissions are enabled</li>
        <li><strong>IP Restricted:</strong> Add Cloudflare IPs or disable IP restriction</li>
        <li><strong>Timestamp Error:</strong> Ensure your system clock is synchronized</li>
      </ul>
    `
  },
  'kucoin': {
    title: 'KuCoin Integration',
    content: `
      <h2>KuCoin Integration</h2>
      <p>KuCoin is a popular cryptocurrency exchange known for listing new tokens early. The platform supports KuCoin Spot and Futures trading.</p>
      
      <h3>Setting Up KuCoin API</h3>
      <ol>
        <li>Log in to your KuCoin account</li>
        <li>Go to <strong>Account → API Management</strong></li>
        <li>Click <strong>Create API</strong></li>
        <li>Set a name and create a <strong>Passphrase</strong> (remember this!)</li>
        <li>Complete security verification</li>
        <li>Copy your API Key, Secret Key, and Passphrase</li>
      </ol>
      
      <h3>API Permissions</h3>
      <ul>
        <li><strong>General:</strong> Required for account info</li>
        <li><strong>Trade:</strong> Required for placing orders</li>
        <li><strong>Futures:</strong> Required for futures trading</li>
        <li><strong>No Withdraw:</strong> Keep disabled for security</li>
      </ul>
      
      <h3>Configuration in AI Trading System</h3>
      <ol>
        <li>Go to Settings → API Connections → KuCoin</li>
        <li>Enter your API Key</li>
        <li>Enter your Secret Key</li>
        <li>Enter your Passphrase (created during API setup)</li>
        <li>Click "Test Connection" to verify</li>
      </ol>
      
      <h3>Important: Passphrase</h3>
      <p>KuCoin requires a passphrase in addition to API key and secret. This passphrase is set by you when creating the API key. Store it securely - you cannot recover it from KuCoin.</p>
      
      <h3>Supported Features</h3>
      <ul>
        <li><strong>Spot Trading:</strong> 700+ trading pairs</li>
        <li><strong>Futures Trading:</strong> Leveraged perpetual contracts</li>
        <li><strong>Market Data:</strong> Real-time prices and order books</li>
        <li><strong>Order Types:</strong> Market, limit, stop orders</li>
      </ul>
      
      <h3>Rate Limits</h3>
      <ul>
        <li><strong>Public Endpoints:</strong> 100 requests per 10 seconds</li>
        <li><strong>Private Endpoints:</strong> 200 requests per 10 seconds</li>
        <li><strong>Order Placement:</strong> 45 orders per 3 seconds</li>
      </ul>
      
      <h3>Troubleshooting</h3>
      <ul>
        <li><strong>Invalid Passphrase:</strong> Passphrase must match exactly what you set</li>
        <li><strong>Signature Error:</strong> Check all three credentials are correct</li>
        <li><strong>Permission Denied:</strong> Verify Trade permission is enabled</li>
      </ul>
    `
  },
  'bybit': {
    title: 'Bybit Integration',
    content: `
      <h2>Bybit Integration</h2>
      <p>Bybit is a leading cryptocurrency derivatives exchange known for its perpetual contracts and copy trading features.</p>
      
      <h3>Setting Up Bybit API</h3>
      <ol>
        <li>Log in to your Bybit account</li>
        <li>Go to <strong>Account → API</strong></li>
        <li>Click <strong>Create New Key</strong></li>
        <li>Select <strong>System-generated API Keys</strong></li>
        <li>Set permissions and complete verification</li>
        <li>Copy your API Key and Secret Key</li>
      </ol>
      
      <h3>API Permissions</h3>
      <ul>
        <li><strong>Read-Write:</strong> Required for trading</li>
        <li><strong>Contract:</strong> For derivatives trading</li>
        <li><strong>Spot:</strong> For spot trading</li>
        <li><strong>No Withdrawal:</strong> Keep disabled</li>
      </ul>
      
      <h3>Configuration</h3>
      <ol>
        <li>Go to Settings → API Connections → Bybit</li>
        <li>Enter your API Key and Secret Key</li>
        <li>Select account type (Unified or Standard)</li>
        <li>Click "Test Connection" to verify</li>
      </ol>
      
      <h3>Supported Features</h3>
      <ul>
        <li><strong>Perpetual Contracts:</strong> USDT and inverse perpetuals</li>
        <li><strong>Spot Trading:</strong> Direct crypto purchases</li>
        <li><strong>Leverage:</strong> Up to 100x on select pairs</li>
      </ul>
    `
  },
  'okx': {
    title: 'OKX Integration',
    content: `
      <h2>OKX Integration</h2>
      <p>OKX (formerly OKEx) is a major cryptocurrency exchange offering spot, futures, and options trading.</p>
      
      <h3>Setting Up OKX API</h3>
      <ol>
        <li>Log in to your OKX account</li>
        <li>Go to <strong>Account → API</strong></li>
        <li>Click <strong>Create API Key</strong></li>
        <li>Set a name and create a <strong>Passphrase</strong></li>
        <li>Select permissions and complete verification</li>
        <li>Copy your API Key, Secret Key, and Passphrase</li>
      </ol>
      
      <h3>API Permissions</h3>
      <ul>
        <li><strong>Read:</strong> Required for market data</li>
        <li><strong>Trade:</strong> Required for order placement</li>
        <li><strong>No Withdraw:</strong> Keep disabled for security</li>
      </ul>
      
      <h3>Configuration</h3>
      <ol>
        <li>Go to Settings → API Connections → OKX</li>
        <li>Enter your API Key, Secret Key, and Passphrase</li>
        <li>Click "Test Connection" to verify</li>
      </ol>
      
      <h3>Supported Features</h3>
      <ul>
        <li><strong>Spot Trading:</strong> 300+ trading pairs</li>
        <li><strong>Perpetual Swaps:</strong> USDT-margined contracts</li>
        <li><strong>Futures:</strong> Quarterly and bi-quarterly contracts</li>
      </ul>
    `
  },
  'telegram-reporting': {
    title: 'Telegram Reporting',
    content: `
      <h2>Telegram Reporting</h2>
      <p>Receive real-time trade notifications and daily summaries via Telegram.</p>
      
      <h3>Setting Up Telegram Bot</h3>
      <ol>
        <li>Open Telegram and search for <strong>@BotFather</strong></li>
        <li>Send <code>/newbot</code> to create a new bot</li>
        <li>Follow the prompts to name your bot</li>
        <li>Copy the <strong>Bot Token</strong> provided</li>
      </ol>
      
      <h3>Getting Your Chat ID</h3>
      <ol>
        <li>Start a chat with your new bot</li>
        <li>Send any message to the bot</li>
        <li>Visit: <code>https://api.telegram.org/bot&lt;TOKEN&gt;/getUpdates</code></li>
        <li>Find your <strong>chat.id</strong> in the response</li>
      </ol>
      
      <h3>Configuration</h3>
      <ol>
        <li>Go to Settings → API Connections → Telegram</li>
        <li>Enter your Bot Token</li>
        <li>Enter your Chat ID</li>
        <li>Click "Test Connection" to send a test message</li>
      </ol>
      
      <h3>Notification Types</h3>
      <ul>
        <li><strong>Trade Opened:</strong> New position entry with details</li>
        <li><strong>Trade Closed:</strong> Position exit with P&L</li>
        <li><strong>Stop Loss Hit:</strong> Position closed at stop loss</li>
        <li><strong>Take Profit Hit:</strong> Position closed at take profit</li>
        <li><strong>Rescue Triggered:</strong> Scale-in rescue activated</li>
        <li><strong>Daily Summary:</strong> End-of-day performance report</li>
      </ul>
      
      <h3>Message Format</h3>
      <p>Trade notifications include:</p>
      <ul>
        <li>Symbol and direction (BUY/SELL)</li>
        <li>Entry/exit price</li>
        <li>Position size and leverage</li>
        <li>P&L amount and percentage</li>
        <li>Strategy name</li>
        <li>Close reason</li>
      </ul>
      
      <h3>Customization</h3>
      <p>Configure which notifications to receive:</p>
      <ul>
        <li>Enable/disable specific notification types</li>
        <li>Set minimum P&L threshold for notifications</li>
        <li>Configure daily summary time</li>
      </ul>
    `
  },
  'webhooks': {
    title: 'Webhooks',
    content: `
      <h2>Webhooks</h2>
      <p>Webhooks allow external services to trigger actions in the AI Trading System or receive notifications about trading events.</p>
      
      <h3>Incoming Webhooks</h3>
      <p>Receive signals from external sources:</p>
      <ul>
        <li><strong>Custom Alerts:</strong> Trigger trades from external systems</li>
        <li><strong>Signal Services:</strong> Integrate third-party signal providers</li>
        <li><strong>Custom Scripts:</strong> Send signals from your own code</li>
      </ul>
      
      <h3>Webhook URL</h3>
      <p>Your webhook endpoint:</p>
      <pre><code>POST https://strategy-engine.houman-ghavamzadeh.workers.dev/api/webhook/{strategyId}</code></pre>
      
      <h3>Webhook Payload</h3>
      <pre><code>{
  "action": "BUY" | "SELL" | "CLOSE",
  "symbol": "BTC-USDT",
  "price": 50000,
  "size": 0.1,
  "secret": "your-webhook-secret"
}</code></pre>
      
      <h3>Authentication</h3>
      <p>Webhooks are authenticated using a secret key:</p>
      <ol>
        <li>Generate a webhook secret in Settings</li>
        <li>Include the secret in your webhook payload</li>
        <li>The system validates the secret before processing</li>
      </ol>
      
      <h3>Outgoing Webhooks</h3>
      <p>Send notifications to external services:</p>
      <ul>
        <li>Configure webhook URLs in Settings</li>
        <li>Select which events trigger webhooks</li>
        <li>Receive JSON payloads with trade details</li>
      </ul>
    `
  },
  'first-strategy': {
    title: 'Your First Strategy',
    content: `
      <h2>Your First Strategy</h2>
      <p>Follow this guide to create and run your first automated trading strategy.</p>
      
      <h3>Step 1: Connect an Exchange</h3>
      <ol>
        <li>Go to Settings → API Connections</li>
        <li>Select your exchange (e.g., Binance)</li>
        <li>Enter your API credentials</li>
        <li>Test the connection</li>
      </ol>
      
      <h3>Step 2: Create a Strategy</h3>
      <ol>
        <li>Go to Strategy Builder</li>
        <li>Click "New Strategy"</li>
        <li>Name your strategy (e.g., "My First Strategy")</li>
      </ol>
      
      <h3>Step 3: Add Nodes</h3>
      <ol>
        <li>Drag a <strong>Data Source</strong> node onto the canvas</li>
        <li>Configure it with your exchange and trading pairs</li>
        <li>Add a <strong>Technical Analyst</strong> node</li>
        <li>Add a <strong>Risk Manager</strong> node</li>
        <li>Add an <strong>Execution</strong> node</li>
      </ol>
      
      <h3>Step 4: Connect Nodes</h3>
      <ol>
        <li>Connect Data Source output to Technical Analyst input</li>
        <li>Connect Technical Analyst output to Risk Manager input</li>
        <li>Connect Risk Manager output to Execution input</li>
      </ol>
      
      <h3>Step 5: Configure Parameters</h3>
      <ol>
        <li>Click each node to open its settings</li>
        <li>Set stop-loss to 1.5%</li>
        <li>Set take-profit to 2.5%</li>
        <li>Set position size to $100</li>
        <li>Set leverage to 5x (or 1x for no leverage)</li>
      </ol>
      
      <h3>Step 6: Save and Test</h3>
      <ol>
        <li>Click "Save Strategy"</li>
        <li>Click "Start Strategy"</li>
        <li>Select "Paper Trading" mode</li>
        <li>Confirm to start</li>
      </ol>
      
      <h3>Step 7: Monitor</h3>
      <ol>
        <li>Go to Trading Dashboard to see your strategy running</li>
        <li>Watch the Strategy Monitor for real-time events</li>
        <li>Check Trade History for completed trades</li>
      </ol>
      
      <h3>Tips for Beginners</h3>
      <ul>
        <li><strong>Start with Paper Trading:</strong> Always test with simulated money first</li>
        <li><strong>Use Conservative Settings:</strong> Start with low leverage and tight stops</li>
        <li><strong>Monitor Closely:</strong> Watch your first few trades carefully</li>
        <li><strong>Start Small:</strong> Use small position sizes until confident</li>
      </ul>
    `
  },
  'paper-trading': {
    title: 'Paper Trading',
    content: `
      <h2>Paper Trading</h2>
      <p>Paper trading allows you to test strategies with simulated money before risking real capital.</p>
      
      <h3>How Paper Trading Works</h3>
      <ul>
        <li><strong>Real Market Data:</strong> Uses live prices from exchanges</li>
        <li><strong>Simulated Execution:</strong> Orders are simulated, not sent to exchange</li>
        <li><strong>Virtual Balance:</strong> Tracks a simulated account balance</li>
        <li><strong>Real P&L Tracking:</strong> Calculates profit/loss as if trades were real</li>
      </ul>
      
      <h3>Starting Paper Trading</h3>
      <ol>
        <li>Create and configure your strategy</li>
        <li>Click "Start Strategy"</li>
        <li>Select "Paper Trading" mode</li>
        <li>Confirm to start</li>
      </ol>
      
      <h3>Paper vs Live Indicators</h3>
      <p>The platform clearly indicates trading mode:</p>
      <ul>
        <li><strong>Paper Mode:</strong> Yellow "PAPER" badge on strategy cards</li>
        <li><strong>Live Mode:</strong> Green "LIVE" badge on strategy cards</li>
        <li><strong>Position Labels:</strong> Positions show "Paper" or "Live" tag</li>
      </ul>
      
      <h3>Switching to Live</h3>
      <ol>
        <li>Stop your paper trading strategy</li>
        <li>Review your paper trading results</li>
        <li>If satisfied, click "Start Strategy"</li>
        <li>Select "Live Trading" mode</li>
        <li>Confirm (requires connected exchange with funds)</li>
      </ol>
      
      <h3>Paper Trading Limitations</h3>
      <ul>
        <li><strong>No Slippage:</strong> Paper trades execute at exact prices</li>
        <li><strong>No Fees:</strong> Trading fees are estimated, not exact</li>
        <li><strong>Instant Fills:</strong> Orders fill immediately (real markets may not)</li>
        <li><strong>No Liquidity Issues:</strong> Large orders always fill in paper mode</li>
      </ul>
      
      <h3>Best Practices</h3>
      <ul>
        <li>Run paper trading for at least 1-2 weeks</li>
        <li>Test in different market conditions</li>
        <li>Track your paper trading results carefully</li>
        <li>Don't switch to live until consistently profitable</li>
      </ul>
    `
  },
  'going-live': {
    title: 'Going Live',
    content: `
      <h2>Going Live</h2>
      <p>Ready to trade with real money? Follow this checklist before going live.</p>
      
      <h3>Pre-Live Checklist</h3>
      <ul>
        <li>☐ Paper traded for at least 2 weeks</li>
        <li>☐ Achieved consistent profitability in paper mode</li>
        <li>☐ Understand all strategy parameters</li>
        <li>☐ Set appropriate stop-loss levels</li>
        <li>☐ Configured rescue system (if using)</li>
        <li>☐ Set up Telegram notifications</li>
        <li>☐ Funded exchange account</li>
        <li>☐ Verified API permissions</li>
      </ul>
      
      <h3>Starting Live Trading</h3>
      <ol>
        <li>Ensure exchange account has sufficient funds</li>
        <li>Open your tested strategy</li>
        <li>Click "Start Strategy"</li>
        <li>Select "Live Trading"</li>
        <li>Review the confirmation dialog carefully</li>
        <li>Click "Confirm" to start live trading</li>
      </ol>
      
      <h3>Risk Management for Live</h3>
      <ul>
        <li><strong>Start Small:</strong> Use 10-20% of your intended size initially</li>
        <li><strong>Tight Stops:</strong> Keep stop-losses conservative (1-2%)</li>
        <li><strong>Low Leverage:</strong> Start with 3-5x, not maximum</li>
        <li><strong>Limited Positions:</strong> Set max concurrent positions to 2-3</li>
      </ul>
      
      <h3>Monitoring Live Trades</h3>
      <ul>
        <li>Check the Trading Dashboard regularly</li>
        <li>Enable Telegram notifications for all trades</li>
        <li>Review the Strategy Monitor for any errors</li>
        <li>Check Trade History daily</li>
      </ul>
      
      <h3>Emergency Procedures</h3>
      <ul>
        <li><strong>Stop Strategy:</strong> Click "Stop" to halt new trades</li>
        <li><strong>Close All Positions:</strong> Use "Close All" for emergency exit</li>
        <li><strong>Disable API:</strong> Revoke API key on exchange if needed</li>
      </ul>
      
      <h3>Scaling Up</h3>
      <p>After successful live trading:</p>
      <ol>
        <li>Review 1-2 weeks of live results</li>
        <li>Gradually increase position sizes</li>
        <li>Slowly increase max concurrent positions</li>
        <li>Consider adding more trading pairs</li>
      </ol>
    `
  },
  'scalping-guide': {
    title: 'Scalping Strategies',
    content: `
      <h2>Scalping Strategies</h2>
      <p>Scalping involves making many small trades to capture quick profits. This guide covers how to build effective scalping strategies.</p>
      
      <h3>Scalping Characteristics</h3>
      <ul>
        <li><strong>Trade Duration:</strong> Seconds to minutes</li>
        <li><strong>Profit Target:</strong> 0.5% - 2% per trade</li>
        <li><strong>Trade Frequency:</strong> Many trades per day</li>
        <li><strong>Leverage:</strong> Often 5x-10x to amplify small moves</li>
      </ul>
      
      <h3>Recommended Settings</h3>
      <ul>
        <li><strong>Stop-Loss:</strong> 1.0% - 1.5%</li>
        <li><strong>Take-Profit:</strong> 1.5% - 2.5%</li>
        <li><strong>Trailing Stop:</strong> 0.4% - 0.6%</li>
        <li><strong>Scan Interval:</strong> 1m or 5m</li>
        <li><strong>Confidence Threshold:</strong> 68% - 72%</li>
      </ul>
      
      <h3>Best Practices</h3>
      <ul>
        <li><strong>High Liquidity Pairs:</strong> Trade BTC, ETH, and major pairs only</li>
        <li><strong>Avoid News Events:</strong> Pause during high-impact announcements</li>
        <li><strong>Session Timing:</strong> Trade during high-volume market hours</li>
        <li><strong>Quick Exits:</strong> Don't hold losing scalps - cut quickly</li>
      </ul>
      
      <h3>Scalp Mode Preset</h3>
      <p>Use the "Many Small Wins Scalp 5x" preset for optimized scalping settings:</p>
      <ul>
        <li>Aggressive entry with quick exits</li>
        <li>Tight stop-losses</li>
        <li>Fast trailing activation</li>
        <li>Disabled trend filters for more entries</li>
      </ul>
    `
  },
  'trend-following': {
    title: 'Trend Following',
    content: `
      <h2>Trend Following Strategies</h2>
      <p>Trend following strategies aim to capture larger moves by riding established trends.</p>
      
      <h3>Trend Following Characteristics</h3>
      <ul>
        <li><strong>Trade Duration:</strong> Hours to days</li>
        <li><strong>Profit Target:</strong> 5% - 20%+ per trade</li>
        <li><strong>Trade Frequency:</strong> Few trades per week</li>
        <li><strong>Leverage:</strong> 3x-5x for safety</li>
      </ul>
      
      <h3>Recommended Settings</h3>
      <ul>
        <li><strong>Stop-Loss:</strong> 2% - 3%</li>
        <li><strong>Take-Profit:</strong> 5% - 10%</li>
        <li><strong>Trailing Stop:</strong> 1% - 2%</li>
        <li><strong>Scan Interval:</strong> 15m or 1h</li>
        <li><strong>Confidence Threshold:</strong> 75% - 80%</li>
      </ul>
      
      <h3>Trend Identification</h3>
      <ul>
        <li><strong>Higher Timeframe Confirmation:</strong> Use 4h and daily charts</li>
        <li><strong>Moving Averages:</strong> Price above 50 EMA and 200 EMA</li>
        <li><strong>ADX:</strong> Above 25 indicates strong trend</li>
        <li><strong>MACD:</strong> Histogram expanding in trend direction</li>
      </ul>
      
      <h3>Entry Timing</h3>
      <ul>
        <li><strong>Pullbacks:</strong> Enter on retracements to support</li>
        <li><strong>Breakouts:</strong> Enter on confirmed breakouts with volume</li>
        <li><strong>Momentum:</strong> Enter when momentum aligns with trend</li>
      </ul>
      
      <h3>Trend Mode Preset</h3>
      <p>Use the "Fewer Larger Wins Trend 5x" preset for optimized trend settings:</p>
      <ul>
        <li>Stricter entry filters</li>
        <li>Wider stops for trend room</li>
        <li>Higher take-profit targets</li>
        <li>HTF trend confirmation enabled</li>
      </ul>
    `
  },
  'api-reference': {
    title: 'API Reference',
    content: `
      <h2>API Reference</h2>
      <p>Complete reference for the AI Trading System REST API.</p>
      
      <h3>Base URL</h3>
      <pre><code>https://strategy-engine.houman-ghavamzadeh.workers.dev/api</code></pre>
      
      <h3>Authentication</h3>
      <p>All API requests require a JWT token in the Authorization header:</p>
      <pre><code>Authorization: Bearer &lt;your-jwt-token&gt;</code></pre>
      
      <h3>Strategy Endpoints</h3>
      <ul>
        <li><code>GET /strategies</code> - List all strategies</li>
        <li><code>GET /strategies/:id</code> - Get strategy details</li>
        <li><code>POST /strategies</code> - Create new strategy</li>
        <li><code>PUT /strategies/:id</code> - Update strategy</li>
        <li><code>DELETE /strategies/:id</code> - Delete strategy</li>
        <li><code>POST /strategies/:id/schedule</code> - Start/update schedule</li>
        <li><code>DELETE /strategies/:id/schedule</code> - Stop schedule</li>
      </ul>
      
      <h3>Position Endpoints</h3>
      <ul>
        <li><code>GET /positions/all</code> - List all positions</li>
        <li><code>POST /positions/:id/close</code> - Close position</li>
        <li><code>POST /positions/close-all</code> - Close all positions</li>
        <li><code>POST /positions/:id/add-budget</code> - Add budget to position</li>
      </ul>
      
      <h3>Account Endpoints</h3>
      <ul>
        <li><code>GET /account</code> - Get account settings</li>
        <li><code>PUT /account</code> - Update account settings</li>
        <li><code>POST /account/password</code> - Change password</li>
      </ul>
      
      <h3>API Connection Endpoints</h3>
      <ul>
        <li><code>GET /api-connections</code> - List API connections</li>
        <li><code>POST /api-connections</code> - Save API connection</li>
        <li><code>POST /api-connections/test</code> - Test connection</li>
      </ul>
      
      <h3>Error Responses</h3>
      <pre><code>{
  "error": "Error message",
  "code": "ERROR_CODE",
  "status": 400
}</code></pre>
    `
  },
  'performance': {
    title: 'Performance Optimization',
    content: `
      <h2>Performance Optimization</h2>
      <p>Tips for optimizing your trading system performance.</p>
      
      <h3>Strategy Optimization</h3>
      <ul>
        <li><strong>Limit Trading Pairs:</strong> Focus on 5-10 high-liquidity pairs</li>
        <li><strong>Reduce Scan Frequency:</strong> Use 5m instead of 1m for less API calls</li>
        <li><strong>Optimize Indicators:</strong> Use fewer, more effective indicators</li>
        <li><strong>Limit Concurrent Positions:</strong> 3-5 positions is usually optimal</li>
      </ul>
      
      <h3>Browser Performance</h3>
      <ul>
        <li><strong>Close Unused Tabs:</strong> Free up browser memory</li>
        <li><strong>Clear Cache:</strong> Periodically clear browser cache</li>
        <li><strong>Use Chrome/Edge:</strong> Best performance with Chromium browsers</li>
        <li><strong>Disable Extensions:</strong> Some extensions can slow down the app</li>
      </ul>
      
      <h3>API Rate Limits</h3>
      <p>Avoid hitting exchange rate limits:</p>
      <ul>
        <li><strong>Binance:</strong> 1200 requests/minute</li>
        <li><strong>KuCoin:</strong> 200 requests/10 seconds</li>
        <li><strong>Reduce Pairs:</strong> Fewer pairs = fewer API calls</li>
        <li><strong>Increase Intervals:</strong> Longer scan intervals reduce load</li>
      </ul>
      
      <h3>Strategy Monitor</h3>
      <ul>
        <li><strong>Filter Events:</strong> Filter to relevant events only</li>
        <li><strong>Limit History:</strong> Clear old events periodically</li>
        <li><strong>Pause When Away:</strong> Pause monitoring when not watching</li>
      </ul>
    `
  },
  'security-best-practices': {
    title: 'Security Best Practices',
    content: `
      <h2>Security Best Practices</h2>
      <p>Keep your trading account and funds secure with these best practices.</p>
      
      <h3>API Key Security</h3>
      <ul>
        <li><strong>Disable Withdrawals:</strong> Never enable withdrawal permissions on trading API keys</li>
        <li><strong>IP Restrictions:</strong> Use IP whitelisting when possible</li>
        <li><strong>Separate Keys:</strong> Use different API keys for different purposes</li>
        <li><strong>Regular Rotation:</strong> Rotate API keys periodically</li>
      </ul>
      
      <h3>Account Security</h3>
      <ul>
        <li><strong>Strong Password:</strong> Use a unique, complex password</li>
        <li><strong>2FA on Exchanges:</strong> Enable 2FA on all exchange accounts</li>
        <li><strong>Secure Email:</strong> Use a secure email with 2FA</li>
        <li><strong>Regular Audits:</strong> Review account activity regularly</li>
      </ul>
      
      <h3>Trading Security</h3>
      <ul>
        <li><strong>Paper Trade First:</strong> Always test strategies before live trading</li>
        <li><strong>Start Small:</strong> Begin with small position sizes</li>
        <li><strong>Set Limits:</strong> Configure maximum position sizes and exposure</li>
        <li><strong>Monitor Actively:</strong> Watch your strategies, especially initially</li>
      </ul>
      
      <h3>Platform Security</h3>
      <ul>
        <li><strong>HTTPS Only:</strong> Always access via HTTPS</li>
        <li><strong>Logout:</strong> Log out when finished</li>
        <li><strong>Private Browsing:</strong> Use private/incognito mode on shared computers</li>
        <li><strong>Keep Updated:</strong> Use the latest browser version</li>
      </ul>
      
      <h3>Emergency Procedures</h3>
      <ul>
        <li><strong>Stop All Strategies:</strong> Know how to quickly stop all trading</li>
        <li><strong>Revoke API Keys:</strong> Know how to revoke keys on each exchange</li>
        <li><strong>Contact Support:</strong> Have exchange support contacts ready</li>
      </ul>
    `
  },
  'account-settings': {
    title: 'Account Settings',
    content: `
      <h2>Account Settings</h2>
      <p>Manage your account profile, security, and preferences.</p>
      
      <h3>Profile Settings</h3>
      <ul>
        <li><strong>Display Name:</strong> Your name shown in the platform</li>
        <li><strong>Email:</strong> Your login email (cannot be changed)</li>
        <li><strong>Timezone:</strong> For displaying times in your local zone</li>
      </ul>
      
      <h3>Security Settings</h3>
      <ul>
        <li><strong>Change Password:</strong> Update your account password</li>
        <li><strong>Session Management:</strong> View and revoke active sessions</li>
        <li><strong>API Keys:</strong> Manage platform API keys (for external access)</li>
      </ul>
      
      <h3>Notification Preferences</h3>
      <ul>
        <li><strong>Email Notifications:</strong> Trade alerts, system updates</li>
        <li><strong>Telegram Notifications:</strong> Real-time trade notifications</li>
        <li><strong>Browser Notifications:</strong> In-app alerts</li>
      </ul>
      
      <h3>Platform Preferences</h3>
      <ul>
        <li><strong>Theme:</strong> Dark mode (default) or light mode</li>
        <li><strong>Default Exchange:</strong> Your preferred exchange</li>
        <li><strong>Currency Display:</strong> USD, EUR, or crypto</li>
      </ul>
      
      <h3>Data Management</h3>
      <ul>
        <li><strong>Export Data:</strong> Download your trade history</li>
        <li><strong>Clear History:</strong> Remove old trade data</li>
        <li><strong>Delete Account:</strong> Permanently delete your account</li>
      </ul>
    `
  },
  'support': {
    title: 'Getting Support',
    content: `
      <h2>Getting Support</h2>
      <p>Need help? Here's how to get support for the AI Trading System.</p>
      
      <h3>Support Tickets</h3>
      <ol>
        <li>Go to <strong>Support</strong> in the sidebar</li>
        <li>Click <strong>New Ticket</strong></li>
        <li>Select a category (Technical, Billing, Feature Request)</li>
        <li>Describe your issue in detail</li>
        <li>Submit the ticket</li>
      </ol>
      
      <h3>What to Include</h3>
      <ul>
        <li>Strategy name and ID (if applicable)</li>
        <li>Steps to reproduce the issue</li>
        <li>Screenshots or error messages</li>
        <li>Browser and device information</li>
        <li>Time when the issue occurred</li>
      </ul>
      
      <h3>Response Times</h3>
      <ul>
        <li><strong>Critical Issues:</strong> Within 4 hours</li>
        <li><strong>High Priority:</strong> Within 24 hours</li>
        <li><strong>Normal:</strong> Within 48 hours</li>
        <li><strong>Low Priority:</strong> Within 1 week</li>
      </ul>
      
      <h3>Self-Help Resources</h3>
      <ul>
        <li><strong>Documentation:</strong> This documentation covers most topics</li>
        <li><strong>FAQs:</strong> Common questions and answers</li>
        <li><strong>Strategy Monitor:</strong> Check for error events</li>
        <li><strong>System Status:</strong> Check if there are known issues</li>
      </ul>
    `
  }
};

function Documentation() {
  const [selectedSection, setSelectedSection] = useState('introduction');
  const [openSections, setOpenSections] = useState({});
  const [drawerOpen, setDrawerOpen] = useState(true);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Find the current section and subsection
  const currentMainSection = docSections.find(section => 
    section.subsections.some(subsection => subsection.id === selectedSection)
  );
  
  const currentSubsection = currentMainSection?.subsections.find(
    subsection => subsection.id === selectedSection
  );

  // Initialize open sections object when component loads
  React.useEffect(() => {
    // Find which section contains the selected subsection and open it
    if (currentMainSection && !openSections[currentMainSection.id]) {
      setOpenSections(prev => ({
        ...prev,
        [currentMainSection.id]: true
      }));
    }
  }, [selectedSection, currentMainSection, openSections]);

  // Handle toggle for section collapse
  const handleToggle = (sectionId) => {
    setOpenSections({
      ...openSections,
      [sectionId]: !openSections[sectionId]
    });
  };

  // Handle selecting a documentation section
  const handleSelectSection = (sectionId) => {
    setSelectedSection(sectionId);
    // If on mobile, close the drawer after selection
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box sx={{
        mb: 3, display: 'flex', alignItems: 'center',
        p: 2, borderRadius: 2,
        background: 'linear-gradient(135deg, rgba(11,17,32,0.85) 0%, rgba(24,27,33,0.9) 100%)',
        backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
        border: '1px solid rgba(255,255,255,0.06)',
      }}>
        <MenuBookIcon sx={{ fontSize: 28, mr: 1, color: '#00FF94' }} />
        <Typography sx={{ fontWeight: 700, fontSize: '1.3rem', color: 'white' }}>
          Documentation
        </Typography>
        {isMobile && (
          <IconButton 
            onClick={() => setDrawerOpen(!drawerOpen)} 
            sx={{ ml: 'auto', color: '#9CA3AF' }}
          >
            <MenuIcon />
          </IconButton>
        )}
      </Box>

      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link color="inherit" href="#" onClick={() => {}}>
          Documentation
        </Link>
        {currentMainSection && (
          <Link color="inherit" href="#" onClick={() => {}}>
            {currentMainSection.title}
          </Link>
        )}
        {currentSubsection && (
          <Typography color="text.primary">{currentSubsection.title}</Typography>
        )}
      </Breadcrumbs>

      <Grid container spacing={3} sx={{ flexGrow: 1 }}>
        {/* Left sidebar */}
        <Grid 
          item 
          xs={12} 
          md={3} 
          sx={{ 
            display: isMobile ? (drawerOpen ? 'block' : 'none') : 'block',
            position: isMobile ? 'absolute' : 'relative',
            zIndex: isMobile ? 10 : 1,
            width: isMobile ? '100%' : 'auto',
            height: isMobile ? '100%' : 'auto',
            top: isMobile ? 0 : 'auto',
            left: isMobile ? 0 : 'auto',
            bgcolor: 'background.paper',
            borderRight: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Paper 
            sx={{ 
              p: 0, 
              height: '100%', 
              borderRadius: 0,
              overflowY: 'auto',
              maxHeight: 'calc(100vh - 180px)'
            }}
          >
            <List component="nav" dense>
              {docSections.map((section) => (
                <React.Fragment key={section.id}>
                  <ListItem disablePadding>
                    <ListItemButton onClick={() => handleToggle(section.id)}>
                      <ListItemIcon>
                        {section.icon}
                      </ListItemIcon>
                      <ListItemText primary={section.title} />
                      {openSections[section.id] ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                  </ListItem>
                  <Collapse in={openSections[section.id] || false} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {section.subsections.map((subsection) => (
                        <ListItem key={subsection.id} disablePadding>
                          <ListItemButton 
                            sx={{ pl: 4 }}
                            selected={selectedSection === subsection.id}
                            onClick={() => handleSelectSection(subsection.id)}
                          >
                            <ListItemText primary={subsection.title} />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Main content */}
        <Grid item xs={12} md={9}>
          <Paper sx={{ p: 3, height: '100%', overflowY: 'auto', maxHeight: 'calc(100vh - 180px)' }}>
            {docContent[selectedSection] ? (
              <>
                <Typography variant="h4" component="h1" gutterBottom>
                  {docContent[selectedSection].title}
                </Typography>
                <Box 
                  dangerouslySetInnerHTML={{ __html: docContent[selectedSection].content }} 
                  sx={{
                    '& pre': {
                      backgroundColor: 'rgba(0,0,0,0.1)',
                      p: 2,
                      borderRadius: 1,
                      overflowX: 'auto'
                    },
                    '& code': {
                      fontFamily: 'monospace'
                    },
                    '& h2': {
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      pb: 1,
                      mb: 2
                    },
                    '& ul, & ol': {
                      pl: 3
                    }
                  }}
                />
              </>
            ) : (
              <Card>
                <CardContent>
                  <Typography variant="h5" component="h2">
                    Documentation in Progress
                  </Typography>
                  <Typography variant="body1">
                    This section is currently being developed. Please check back later for updates.
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Documentation;
